import React, { useEffect, useRef, useState } from 'react'
import {
    ApiDTO_NTSPform,
    ApiDTO_NTSPUI_ElementList,
    initialFormState,
    NTSP_StatPortalProps,
    NTSPDevices,
    NTSPElement,
    setWasCompletedOnLoad, //FP-14292 - action to set wasCompletedOnLoad in Redux store after saving form with Completed outcome, so that CPT requirement logic can account for cases that were already Completed on load vs newly marked as Completed during current session
} from './Ntsp_Slice'
import { formatDateTimeLocal, IANAToTzCode, tzCodeToIANA } from '../../Utils'
import PatientDetails from './PatientDetails'
import { PatientDetailsHeader } from './PatientHeader'
import {
    AcuteServiceKey,
    SabacuteServiceKey,
    teleEEGServiceKey,
    validateForm,
    isPatientEducationFollowUp,
    is24HourFollowUp,
    convertEasternToLocal,
    Neurohospitalistkey
} from './NTSP_StatPortal'
import { FoundationsButton } from '../../GenericComponents/button'
import { wrap } from 'module'
import { useDispatch, useSelector } from 'react-redux'
import DatePicker from '../../GenericComponents/DatePicker'
import moment from 'moment'
import Select, { components, GroupProps } from 'react-select'
import { DarkModeColors, LightModeColors, getReactSelectCustomStyles } from './NTSP_StatPortal'
import { usePromiseDebounce } from '../../Hooks/usePromiseDebounce' //FP-12575 Debounce save for UI actions

export const getServiceLineName = (serviceKey: number): string => {
    switch (serviceKey) {
        case AcuteServiceKey:
            return 'Acute Service';
        case SabacuteServiceKey:
            return 'Sabacute Service';
        case teleEEGServiceKey:
            return 'TeleEEG Service';
        case Neurohospitalistkey:
            return 'Neurohospitalist';
        default:
            return 'Service Line';
    }
}

export function getCaseIdSuffix(input: string): string {
    const parts = input.split('-');
    return parts.length > 1 ? parts[parts.length - 1] : input;
}

const isCompletedOrTelephoneOutcome = (etlMatchValue?: string): boolean => {
    const normalizedValue = etlMatchValue?.toLowerCase() || ''
    return normalizedValue === 'completed' || normalizedValue.includes('telephone')
} //FP-14522 - helper function to determine if selected outcome is Completed or any Telephone outcome

type CPTSelectOption = {
    label: string
    value: string
    isFixed?: boolean
}

const getUniqueCPTCodeIds = (codeIds: number[] = []): number[] =>
    Array.from(
        new Set(codeIds.filter((codeId) => Number.isInteger(codeId) && codeId > 0))
    )

const EIGHT_HOURS_IN_MS = 8 * 60 * 60 * 1000
const TIMESTAMP_HAS_TIMEZONE_REGEX = /(Z|[+-]\d{2}:?\d{2})$/i

const parseNoteTimestamp = (timestamp?: string | Date): number | null => {
    if (!timestamp) {
        return null
    }

    if (timestamp instanceof Date) {
        const dateValue = timestamp.getTime()
        return Number.isFinite(dateValue) ? dateValue : null
    }

    if (typeof timestamp !== 'string') {
        return null
    }

    const normalizedTimestamp = timestamp.trim()
    const hasTimezone = TIMESTAMP_HAS_TIMEZONE_REGEX.test(normalizedTimestamp)

    // If backend sends a timestamp without timezone, treat it as UTC for consistent comparisons.
    const strictIso = hasTimezone
        ? moment.parseZone(normalizedTimestamp, moment.ISO_8601, true)
        : moment.tz(normalizedTimestamp, moment.ISO_8601, true, 'America/New_York') //EST time zone

    if (strictIso.isValid()) {
        return strictIso.valueOf()
    }

    const fallbackPrimary = Date.parse(normalizedTimestamp)
    const fallbackAssumeUtc = hasTimezone
        ? fallbackPrimary
        : Date.parse(`${normalizedTimestamp}Z`)
    const fallback = Number.isFinite(fallbackAssumeUtc) ? fallbackAssumeUtc : fallbackPrimary

    return Number.isFinite(fallback) ? fallback : null
}

const isNoteWithinEditWindow = (timestamp?: string | Date, nowMs: number = Date.now()): boolean => {
    const createdAtMs = parseNoteTimestamp(timestamp)

    if (createdAtMs === null || createdAtMs > nowMs) {
        return false
    }

    return nowMs - createdAtMs <= EIGHT_HOURS_IN_MS
}

const isEditableByCurrentUser = (
    note: { PostedByUserId?: number } | undefined,
    currentUserId?: number
): boolean =>
    typeof currentUserId === 'number' &&
    Number.isFinite(currentUserId) &&
    currentUserId > 0 &&
    note?.PostedByUserId === currentUserId

const getProviderNoteCPTCodeIds = (
    providerNotes: NTSP_StatPortalProps['providerNotes'],
    caseId: string,
    currentUserId?: number
): number[] => {
    const currentCaseNotes =
        providerNotes?.find((noteGroup) => noteGroup.CaseId === caseId)?.ProviderCaseNotes || []
    const nowMs = Date.now()
    const shouldFilterByCurrentUser =
        typeof currentUserId === 'number' && Number.isFinite(currentUserId) && currentUserId > 0

    const recentCaseNotes = currentCaseNotes.filter((note) => {
        const createdAtMs = parseNoteTimestamp(note.ts_CreationDateTime)

        if (createdAtMs === null) {
            return false
        }

        const ageMs = nowMs - createdAtMs
        const inFuture = createdAtMs > nowMs
        const withinWindow = ageMs <= EIGHT_HOURS_IN_MS
        const includeCurrentUser =
            !shouldFilterByCurrentUser || note.PostedByUserId === currentUserId
        const include = includeCurrentUser && !inFuture && withinWindow

        return include
    })


    return getUniqueCPTCodeIds(
        recentCaseNotes.reduce((codes: number[], note) => {
            if (Array.isArray(note.fk_CPTCodes)) {
                codes.push(...note.fk_CPTCodes)
            }

            return codes
        }, [])
    )
}

const mapCPTCodeIdsToOptions = (
    codeIds: number[],
    cptCodes: ApiDTO_NTSPUI_ElementList['CPTCodes'] | undefined,
    lockedCodeIds: number[] = []
): CPTSelectOption[] => {
    const lockedCodeIdSet = new Set(lockedCodeIds)

    return getUniqueCPTCodeIds(codeIds).map((codeId) => {
        const cptCode = cptCodes?.find((item) => item._key === codeId)

        return {
            label: cptCode?.FriendlyName || codeId.toString(),
            value: codeId.toString(),
            isFixed: lockedCodeIdSet.has(codeId),
        }
    })
}

const CPTMultiValueRemove = (props: any) => {
    if (props.data?.isFixed) {
        return null
    }

    return <components.MultiValueRemove {...props} />
}

const SymptomGroup = (
    props: GroupProps<
        {
            readonly value: string
            readonly label: string
            readonly color: string
            readonly isFixed?: boolean
            readonly isDisabled?: boolean
        },
        false
    >
) => {
    const groupStyles = {
        borderBottom: `2px solid inherit`,
    }
    return (
        <div style={groupStyles}>
            <components.Group {...props} />
        </div>
    )
}

export const isCPTMandatory = (
    form: any,
    NTSPElements: any,
    wasAlreadyCompleted: boolean = false,
    providerNotes?: NTSP_StatPortalProps['providerNotes'],
    currentUserId?: number
) => {
    const acuteSubacuteServiceKeys = [AcuteServiceKey, SabacuteServiceKey];
    const isFacilitySelected = form.fk_Facility !== -1;
    const isNotTeleEEG = form.fk_ConsultService !== teleEEGServiceKey;

    const facilityObj = NTSPElements?.Facilities?.find(
        (facility: any) =>
            facility._key === form.fk_Facility &&
            (facility.SiteStatus?.toLowerCase() === 'acute and subacute' && facility.IsNeurohospitalist
                ? facility.IsNeurohospitalist
                : true)
    );
    const isFacilityAcuteAndSubacute = Boolean(facilityObj);

    // CPT is mandatory only when the case has no CPT codes recorded in the current 8-hour window.
    // This applies equally to parent and follow-up cases and is independent of case status.
    const isAcuteOrSubacute = acuteSubacuteServiceKeys.includes(form.fk_ConsultService);
    const providerNoteCPTCodes =
        isAcuteOrSubacute && providerNotes
            ? getProviderNoteCPTCodeIds(providerNotes, form.CaseId, currentUserId)
            : [];
    const hasNoProviderNoteCPTInWindow = isAcuteOrSubacute && providerNoteCPTCodes.length === 0;

    const sharedConditions =
        isFacilitySelected && isNotTeleEEG && isFacilityAcuteAndSubacute;

    return sharedConditions && isAcuteOrSubacute && hasNoProviderNoteCPTInWindow;
};

const CaseDetailsForm: React.FC<NTSP_StatPortalProps> = ({
    form,
    setForm,
    NTSPElements,
    handleSave,
    handleClear,
    handleTabSwitch,
    validationSchema,
    errors,
    setErrors,
    isSaved,
    providerNotes,
    nurseNotes,
    selectedSymptoms,
    setSelectedSymptoms,
    currentUserId,
}) => {
    if (errors === undefined) errors = {}
    if (setErrors === undefined) setErrors = (errors: any) => {}
    if (validationSchema === undefined) validationSchema = {}
    if (!selectedSymptoms) { selectedSymptoms = {} }
    if (!setSelectedSymptoms) {
        setSelectedSymptoms = () => {}
    }   
    // const [disableFollowUpCheckboxes, setdisableFollowUpCheckboxes] = useState<boolean>(false)
    type FieldErrors = {
        [key: string]: string
    }

    const [remainigNotes, setRemainingNotes] = useState(2048)
    const [formError, setFormError] = useState<string>('')
    const [submitted, setSubmitted] = useState<boolean>(false)
    const [saving, setSaving] = useState<boolean>(false)
    const [educationTimeReset, setEducationTimeReset] = useState(0)
    const [selectedCaseId, setSelectedCaseId] = useState<string>(form.CaseId);
    const [typeMap, setTypeMap] = useState<{
        [s: string]: { [s: string]: { label: string; value: string }[] }
    }>({})
    // const [selectedSymptoms, setSelectedSymptoms] = useState<{ label: string; value: string }[]>()
    const [isNightMode, setIsNightMode] = useState(document.body.classList.contains('night-mode'))
    const [toggleSymptoms, setOpenSymptoms] = useState(true)
    const [examCompletedModified, setExamCompletedModified] = useState<boolean>(false)
    const [selectedCPTCodes, setSelectedCPTCodes] = useState<CPTSelectOption[]>([])
    const [isMandatory, setIsMandatory] = useState<boolean>(false) //FP-14522 - state to track if CPT Codes are mandatory based on selected outcome and other conditions
    //FP-14500 - helper function for margin top pof assessment plan section
    const getAssessmentPlanMarginTop = (consultService: number | string) => {
        const marginTopByService: Record<number, string> = {
            [SabacuteServiceKey]: '133px',
            [AcuteServiceKey]: '58px',
            [teleEEGServiceKey]: '9px',
        }

        return marginTopByService[Number(consultService)] ?? '62px'
    }
    const [editingProviderNoteId, setEditingProviderNoteId] = useState<number | null>(null)
    const [editingProviderNoteCaseId, setEditingProviderNoteCaseId] = useState<string>('')
    const providerNoteEditFeatureEnabled =
        [AcuteServiceKey, SabacuteServiceKey].includes(form.fk_ConsultService)
    const lockedProviderCPTCodeIds = providerNoteEditFeatureEnabled
        ? getProviderNoteCPTCodeIds(providerNotes, form.CaseId, currentUserId)
        : []
    const editingProviderNoteCPTCodeIds = React.useMemo(() => {
        const currentCaseNotes =
            providerNotes?.find((noteGroup) => noteGroup.CaseId === editingProviderNoteCaseId)
                ?.ProviderCaseNotes || []

        const selectedEditingNote = currentCaseNotes.find((note) => note.Id === editingProviderNoteId)

        return getUniqueCPTCodeIds(
            Array.isArray(selectedEditingNote?.fk_CPTCodes) ? selectedEditingNote.fk_CPTCodes : []
        )
    }, [providerNotes, editingProviderNoteCaseId, editingProviderNoteId])

    const effectiveLockedProviderCPTCodeIds = React.useMemo(() => {
        if (editingProviderNoteId === null || editingProviderNoteCaseId !== form.CaseId) {
            return lockedProviderCPTCodeIds
        }

        const editingCodesSet = new Set(editingProviderNoteCPTCodeIds)
        return lockedProviderCPTCodeIds.filter((codeId) => !editingCodesSet.has(codeId))
    }, [
        lockedProviderCPTCodeIds,
        editingProviderNoteId,
        editingProviderNoteCaseId,
        form.CaseId,
        editingProviderNoteCPTCodeIds,
    ])

    const lockedProviderCPTCodeIdSet = new Set(effectiveLockedProviderCPTCodeIds)

    const dispatch = useDispatch()
    const wasCompletedOnLoad = useSelector((state: any) => state.ntspSlice.wasCompletedOnLoad)
    const followUp24HourValuesRef = useRef<Pick<ApiDTO_NTSPform, 'fk_followUpConsultant' | 'ts_FollowUpHandoff'> | null>(null) //FP-14499 - ref to store 24-hour follow-up consultant and handoff values when toggling 24-hour follow-up options
     const updateSelectedSymptoms = (newValue: any, type: string) => {
        setSelectedSymptoms((prev) => ({
            ...prev,
            [type]: newValue || [],
        }))
        // setSelectedSymptoms(Object.values({ ...mappedSelected, [type]: newValue || [] }).flat())
    }

    // FP-14522 Mandatory indicator: recompute when relevant form fields change
    useEffect(() => {
        setIsMandatory(
            isCPTMandatory(
                form,
                NTSPElements,
                wasCompletedOnLoad,
                providerNotes,
                currentUserId
            )
        )
    }, [
        form.fk_Outcome,
        form.fk_ConsultService,
        form.fk_Facility,
        form.fk_FollowUp,
        NTSPElements,
        providerNotes,
        currentUserId,
        wasCompletedOnLoad,
    ])

    const updateSelectedCPTCodes = (newValue: any) => {
        const editableCPTCodeIds = getUniqueCPTCodeIds(
            (newValue || [])
                .map((item: CPTSelectOption) => Number(item.value))
                .filter((codeId: number) => !lockedProviderCPTCodeIdSet.has(codeId))
        )
        
        const displayedCPTCodes = mapCPTCodeIdsToOptions(
            [...effectiveLockedProviderCPTCodeIds, ...editableCPTCodeIds],
            NTSPElements?.CPTCodes,
            effectiveLockedProviderCPTCodeIds
        )

        setSelectedCPTCodes(displayedCPTCodes)
        setForm((prev: ApiDTO_NTSPform) => ({
            ...prev,
            fk_CPTCodes: editableCPTCodeIds,
        }))
        // Validate NewNote and clear fk_CPTCodes error when CPT Codes change since NewNote depends on fk_CPTCodes
        const newNoteSchema = validationSchema['NewNote']
        const cptCodeSchema = validationSchema['fk_CPTCode']
        setErrors((prev: FieldErrors) => ({
            ...prev,
            fk_CPTCode: cptCodeSchema && typeof cptCodeSchema === 'function'
                ? cptCodeSchema({ ...form, fk_CPTCodes: editableCPTCodeIds }, NTSPElements)
                : '',
            NewNote: newNoteSchema && typeof newNoteSchema === 'function'
                ? newNoteSchema({ ...form, fk_CPTCodes: editableCPTCodeIds }, NTSPElements)
                : ''
        }))
    }

    const customStyles = getReactSelectCustomStyles(isNightMode)

    // Compact custom styles for CPT Code dropdown with reduced height
    const compactCPTStyles = {
        ...customStyles,
        control: (base: any, state: any) => ({
            ...customStyles.control(base, state),
            minHeight: '38px',
            border: `1px solid ${isNightMode ? '#6c757d' : '#495057'}`,
            boxSizing: 'border-box',
             width: '100%', // Responsive: fills parent/container
        maxWidth: '100%',
        }),
        valueContainer: (base: any) => ({
            ...customStyles.valueContainer(base),
            padding: '2px 8px',
        }),
        input: (base: any) => ({
            ...customStyles.input(base),
            margin: '0px',
            padding: '0px',
        }),
        indicatorsContainer: (base: any) => ({
            ...customStyles.indicatorsContainer(base),
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (base: any) => ({
            ...customStyles.dropdownIndicator(base),
            padding: '0 8px',
        }),
        clearIndicator: (base: any) => ({
            ...customStyles.clearIndicator(base),
            padding: '0 8px',
        }),
        multiValue: (base: any, state: any) => ({
            ...base,
            opacity: state.data?.isFixed ? 0.6 : 1,
            backgroundColor: isNightMode
                ? state.data?.isFixed
                    ? '#495057'
                    : '#e6e6e6'
                : state.data?.isFixed
                    ? '#dee2e6'
                    : base.backgroundColor,
            border: `1px solid ${
                isNightMode
                    ? state.data?.isFixed
                        ? '#6c757d'
                        : '#868e96'
                    : state.data?.isFixed
                        ? '#adb5bd'
                        : 'transparent'
            }`,
        }),
        multiValueLabel: (base: any, state: any) => ({
            ...base,
            fontWeight: state.data?.isFixed ? 500 : base.fontWeight,
            color: state.data?.isFixed
                ? isNightMode
                    ? '#fafafa'
                    : '#353535'
                : isNightMode
                    ? '#333333'
                    : base.color,
        }),
        multiValueRemove: (base: any, state: any) => ({
            ...base,
            display: state.data?.isFixed ? 'none' : base.display,
        }),
        menu: (base: any) => ({
            ...customStyles.menu(base),
            maxHeight: '300px',
            width: '100%', // Responsive: matches input width
        maxWidth: '100%',
        }),
        menuList: (base: any) => ({
            ...customStyles.menuList(base),
            maxHeight: '300px',
            overflowY: 'auto',
        }),
    }

    // Add this helper set (after other hooks/util definitions)
    const consultants24HrSet = React.useMemo(
        () => new Set(NTSPElements?.Consultants24Hr?.map((c: NTSPElement) => c._key) || []),
        [NTSPElements?.Consultants24Hr]
    )

    const debouncedHandleSave = usePromiseDebounce(async (...args: Parameters<typeof handleSave>) => {
        return await Promise.resolve(handleSave(...args));
    }); //FP-12575 debounce to prevent multiple rapid clicks
    //FP-14499: New helper function to check 24-hour follow-up requirements and return any validation errors related to that
    const get24HourFollowUpRequiredErrors = (currentForm: ApiDTO_NTSPform): FieldErrors => {
        const followUpErrors: FieldErrors = {}
        const is24HourRequired =
            typeof currentForm.fk_FollowUps !== 'number' &&
            Array.isArray(currentForm.fk_FollowUps) &&
            currentForm.fk_FollowUps.some((id) => is24HourFollowUp(NTSPElements, id))

        if (!is24HourRequired) {
            return followUpErrors
        }

        if (Number(currentForm.fk_followUpConsultant ?? -1) === -1) {
            followUpErrors.fk_followUpConsultant = 'Consultant is required for 24-hour follow-up.'
        }

        if (!currentForm.ts_FollowUpHandoff) {
            followUpErrors.ts_FollowUpHandoff = 'Hand Off date and time is required for 24-hour follow-up.'
        }

        return followUpErrors
    }

    // FP-14522 Add near other local helpers inside CaseDetailsForm
    const hasNoValidCPTCodes = (codes: any): boolean =>
        !Array.isArray(codes) || codes.length === 0 || codes.every((code: number) => code === -1)

    const applyCPTError = (target: { [key: string]: string }) => {
        target.fk_CPTCode = 'At least one CPT Code is required.'
        target.fk_CPTCodes = 'At least one CPT Code is required.'
    }

    const clearCPTError = (target: { [key: string]: string }) => {
        target.fk_CPTCode = ''
        target.fk_CPTCodes = ''
    }

    const getProviderNotesForMandatoryCheck = (currentForm: ApiDTO_NTSPform) =>
        Array.isArray(currentForm.ProviderNotes) ? currentForm.ProviderNotes : providerNotes

    const buildValidationForm = (
        sourceForm: ApiDTO_NTSPform,
        overrides: Record<string, any> = {}
    ) => ({
        ...sourceForm,
        ...overrides,
        _examCompletedModified: examCompletedModified,
        _wasCompletedOnLoad: wasCompletedOnLoad,
        _providerNotes: providerNotes,
    })

    const wrapHandleSave = async () => {
        setSubmitted(true)
        setSaving(true)

        const formForSave = applyProviderNoteEditToForm(form)
        if (formForSave !== form) {
            setForm(formForSave)
            await new Promise((resolve) => setTimeout(resolve, 0))
        }
        
        const tempErrors = validateForm(buildValidationForm(formForSave), validationSchema, NTSPElements)
        const providerNotesForMandatoryCheck = getProviderNotesForMandatoryCheck(formForSave)
        //FP-14499: Check 24-hour follow-up requirements and add any errors to tempErrors
        const followUpRequiredErrors = get24HourFollowUpRequiredErrors(formForSave)
        tempErrors['fk_followUpConsultant'] = followUpRequiredErrors.fk_followUpConsultant || ''
        tempErrors['ts_FollowUpHandoff'] = followUpRequiredErrors.ts_FollowUpHandoff || ''
        
        //FP-14522 Check CPT codes requirement and add to tempErrors
        const cptRequired =
            isCPTMandatory(formForSave, NTSPElements, wasCompletedOnLoad, providerNotesForMandatoryCheck, currentUserId) &&
            hasNoValidCPTCodes(formForSave.fk_CPTCodes)

        if (cptRequired) {
            applyCPTError(tempErrors)
        } else {
            clearCPTError(tempErrors)
        }
        
        // Merge existing errors (including date validation errors) with new validation errors
        const allErrors = { ...errors, ...tempErrors }
        setErrors(allErrors)
        // Check if there are any errors (from validation schema or date validation)
        if (Object.values(allErrors).some(Boolean)) {
            setFormError('Please fix the errors before saving.')
            setSaving(false)
            return
        } else {
            setErrors({})
            setFormError('')
            if (debouncedHandleSave) { //FP-14292 for edit provider notes
                await debouncedHandleSave(false, formForSave)
            } //FP-12575 using debounced handle save to prevent multiple rapid clicks on save button

            if (editingProviderNoteId !== null && editingProviderNoteCaseId) {
                cancelProviderNoteEdit()
            }
            
            // After successful save, if case was marked as "Completed", update the ref
            const selectedOutcome = NTSPElements?.Outcome?.find((outcome: any) => outcome._key === formForSave.fk_Outcome);
            const isCompleted = isCompletedOrTelephoneOutcome(selectedOutcome?.ETLMatchValue); //FP-14522
            if (isCompleted) {
                dispatch(setWasCompletedOnLoad(true))
                setIsMandatory(false) //FP-14522 Update mandatory state since case is now Completed
            }
            
            setSaving(false)
            return
        }
    }
    const wrapHandleClear = async () => {
        setSubmitted(true)
        setSaving(true)
        //FP-14292 for edit provider notes
        const formForSave = applyProviderNoteEditToForm(form)
        if (formForSave !== form) {
            setForm(formForSave)
            await new Promise((resolve) => setTimeout(resolve, 0))
        }
        
        // Exclude NewNote from validation since form will be cleared
        const closeValidationSchema = Object.keys(validationSchema).reduce((acc, key) => {
            // When exam is completed, note text must still be validated on close.
            if (key !== 'NewNote' || formForSave.ExamCompleted) {
                acc[key] = validationSchema[key]
            }
            return acc
        }, {} as typeof validationSchema)
        
        const tempErrors = validateForm(buildValidationForm(formForSave), closeValidationSchema, NTSPElements)//FP-14292  update form -> formForSave with provider note edits before validation
        const providerNotesForMandatoryCheck = getProviderNotesForMandatoryCheck(formForSave)
        //FP-14499: Check 24-hour follow-up requirements and add any errors to tempErrors
        const followUpRequiredErrors = get24HourFollowUpRequiredErrors(formForSave)
        tempErrors['fk_followUpConsultant'] = followUpRequiredErrors.fk_followUpConsultant || ''
        tempErrors['ts_FollowUpHandoff'] = followUpRequiredErrors.ts_FollowUpHandoff || ''
        
        //FP-14522 Check CPT codes requirement and add to tempErrors
        const cptRequired =
            isCPTMandatory(formForSave, NTSPElements, wasCompletedOnLoad, providerNotesForMandatoryCheck, currentUserId) &&
            hasNoValidCPTCodes(formForSave.fk_CPTCodes) //FP-14292 use formForSave which includes provider note edits to check CPT codes since NewNote is being excluded from validation but CPT requirement may depend on NewNote content

        if (cptRequired) {
            applyCPTError(tempErrors)
        } else {
            clearCPTError(tempErrors)
        }
        
        const allErrors = { ...errors, ...tempErrors }
        setErrors(allErrors)
        if (Object.values(allErrors).some(Boolean)) {
            setFormError('Please fix the errors before saving.')
            setSaving(false)
            return
        } else {
            setErrors({})
            setFormError('')
            if (handleSave) await handleSave(true, formForSave)

            if (editingProviderNoteId !== null && editingProviderNoteCaseId) {
                cancelProviderNoteEdit()
            }
            
            // After successful save, if case was marked as "Completed", update the ref
            const selectedOutcome = NTSPElements?.Outcome?.find((outcome: any) => outcome._key === formForSave.fk_Outcome);
            const isCompleted = isCompletedOrTelephoneOutcome(selectedOutcome?.ETLMatchValue); //FP-14522
            if (isCompleted) {
                dispatch(setWasCompletedOnLoad(true))
                setIsMandatory(false) //FP-14522
            }
            
            if (handleClear) handleClear()
            setSaving(false)
            return
        }
    }
    useEffect(() => {
        if (!Object.values(errors) || Object.values(errors).join('').length === 0) {
            setFormError('')
        }
        //FP-14557 
        const hasSelectedFollowUps =
            typeof form.fk_FollowUps !== 'number' &&
            Array.isArray(form.fk_FollowUps) &&
            form.fk_FollowUps.length > 0 &&
            !form.fk_FollowUps.includes(-1)

        if ((form.fk_FollowUp !== -1 || hasSelectedFollowUps) && errors?.fk_FollowupNeeded) {
            setErrors((prev: FieldErrors) => ({
                ...prev,
                fk_FollowupNeeded: '',
            }))
        }
    }, [errors, form.fk_FollowUp, form.fk_FollowUps, setErrors])

    useEffect(() => {
            const handleClassChange = () => {
                setIsNightMode(document.body.classList.contains('night-mode'))
            }
    
            const observer = new MutationObserver(() => {
                handleClassChange()
            })
    
            observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    
            return () => {
                observer.disconnect()
            }
        }, [])

    const [NewNote, setNewNote] = useState<string>('')
    const firstInputRef = useRef(null as any)
    const lastInputRef = useRef(null as any)
    const formRef = useRef(null as any)
    const timezone = useSelector((state: any) => state.ntspSlice.timezone) // adjust slice name if needed

    useEffect(() => {
        setForm((prev: ApiDTO_NTSPform) => ({
            ...prev,
            NewNote: NewNote,
        }))
    }, [NewNote])

     useEffect(() => {
            // const handleTabKeyPress = (event: {
            //     key: string
            //     shiftKey: any
            //     preventDefault: () => void
            // }) => {
            //     if (event.key === 'Tab') {
            //         if (event.shiftKey) {
            //             // Shift + Tab (backward)
            //             if (document.activeElement === firstInputRef.current) {
            //                 event.preventDefault()
            //                 lastInputRef.current?.focus()
            //             }
            //         } else {
            //             // Tab (forward)
            //             if (document.activeElement === lastInputRef.current) {
            //                 event.preventDefault()
            //                 firstInputRef.current?.focus()
            //             }
            //         }
            //     }
            // }
            const allSymptoms = NTSPElements?.Symptoms || []
            const filteredSymptoms = allSymptoms.filter((s)  => {
            
             if(NTSPElements?.SymptomServiceMapping && NTSPElements?.SymptomServiceMapping.length>0 && NTSPElements?.SymptomServiceMapping.findIndex(mapping=>mapping.fk_Symptom===s._key && 
                mapping.ServiceLineEnum===form.fk_ConsultService)!==-1 &&
            s.IsHistory===true){ 
              return true;
            }
            
            return false;
            })
            // const allowedIds = new Set(filteredSymptoms.map((s) => s._key))
            // prune any previously selected symptoms that are no longer allowed
            // const prunedSelected = (form.Symptoms || []).filter((id) => allowedIds.has(id))
            // setSelectedSymptoms(
            //     prunedSelected.map((symptomId) => ({
            //         label:
            //             filteredSymptoms.find((s) => s._key === symptomId)?.FriendlyName ||
            //             symptomId.toString(),
            //         value: symptomId.toString(),
            //     }))
            // )
            const tempTypeMap: { [s: string]: { [s: string]: { label: string; value: string }[] } } = {}
            const tempSymptomTypeMap: { [key: string]: string } = {}
            // const tempCategoryMap : {[s:string]:Set<NTSPElement>} = {}
    
            // type => points to a set of categories => each category maps to array of symptoms in form {label: '', value: ''}
            filteredSymptoms.forEach((symptom) => {
                // Build hierarchy: SymptomType -> categories; category-less symptoms stored under empty key ''
                const categoryRaw = symptom.SymptomCategory || ''
                const type = symptom.SymptomType || ''
                tempSymptomTypeMap[symptom._key + ''] = type
                if (!tempTypeMap[type]) tempTypeMap[type] = {}
                const categoryKey = categoryRaw.trim() === '' ? '' : categoryRaw
                if (tempTypeMap[type][categoryKey]) {
                    tempTypeMap[type][categoryKey] = tempTypeMap[type][categoryKey].concat({
                        label: symptom.FriendlyName,
                        value: symptom._key.toString(),
                    })
                } else {
                    tempTypeMap[type][categoryKey] = [
                        { label: symptom.FriendlyName, value: symptom._key.toString() },
                    ]
                }
            })
            setTypeMap(tempTypeMap)
            // setSymptomTypeMap(tempSymptomTypeMap)
    
            const tempMappedSelected: { [s: string]: { value: string; label: string }[] } = {}
            form.Symptoms?.forEach((symptomId) => {
                const type = tempSymptomTypeMap[symptomId + ''] || ''
                if (!!tempMappedSelected[type]) {
                    tempMappedSelected[type] = tempMappedSelected[type].concat({
                        label:
                            NTSPElements?.Symptoms?.find((s) => s._key === symptomId)?.FriendlyName ||
                            symptomId.toString(),
                        value: symptomId.toString(),
                    })
                } else {
                    tempMappedSelected[type] = [
                        {
                            label:
                                NTSPElements?.Symptoms?.find((s) => s._key === symptomId)
                                    ?.FriendlyName || symptomId.toString(),
                            value: symptomId.toString(),
                        },
                    ]
                }
            })
            setSelectedSymptoms(tempMappedSelected)
    
            // const formElement = formRef.current
            // if (formElement) {
            //     formElement.addEventListener('keydown', handleTabKeyPress)
            // }
    
            // return () => {
            //     if (formElement) {
            //         formElement.removeEventListener('keydown', handleTabKeyPress)
            //     }
            // }
        }, [form.CaseId, form.fk_ConsultService, NTSPElements?.Symptoms])

    // Keep historical provider-note CPT codes visible while leaving new selections editable.
    useEffect(() => {
        const selectedCodes = mapCPTCodeIdsToOptions(
            [...effectiveLockedProviderCPTCodeIds, ...(Array.isArray(form.fk_CPTCodes) ? form.fk_CPTCodes : [])],
            NTSPElements?.CPTCodes,
            effectiveLockedProviderCPTCodeIds
        )

        setSelectedCPTCodes(selectedCodes)
    }, [form.CaseId, form.fk_CPTCodes, NTSPElements?.CPTCodes, providerNotes, effectiveLockedProviderCPTCodeIds])

    useEffect(() => {
        setEditingProviderNoteId(null)
        setEditingProviderNoteCaseId('')
        setForm((prev: ApiDTO_NTSPform) => ({
            ...prev,
            _providerNoteEditMode: false,
        }))
    }, [selectedCaseId])

    const startProviderNoteEdit = (
        caseId: string,
        note: {
            Id: number
            Note: string
            ts_CreationDateTime: string | Date
            fk_CPTCodes?: number[]
            PostedByUserId?: number
        }
    ) => {
        if (!providerNoteEditFeatureEnabled) {
            return
        }

        if (!isEditableByCurrentUser(note, currentUserId)) {
            return
        }

        if (!isNoteWithinEditWindow(note.ts_CreationDateTime)) {
            return
        }

        const editableCPTCodeIds = getUniqueCPTCodeIds(
            Array.isArray(note.fk_CPTCodes) ? note.fk_CPTCodes : []
        )
        const editableText = note.Note || ''

        setEditingProviderNoteCaseId(caseId)
        setEditingProviderNoteId(note.Id)
        setSelectedCaseId(caseId)
        setNewNote(editableText)
        setRemainingNotes(Math.max(0, 2048 - editableText.length))
        setForm((prev: ApiDTO_NTSPform) => ({
            ...prev,
            NewNote: editableText,
            fk_CPTCodes: editableCPTCodeIds,
            _providerNoteEditMode: true,
            _editedProviderNotesPayload: undefined, //FP-14292 - will be set upon applying edit to form, cleared when starting a new edit
        }))
    }

    const cancelProviderNoteEdit = () => {
        setEditingProviderNoteId(null)
        setEditingProviderNoteCaseId('')
        setNewNote('')
        setRemainingNotes(2048)
        setFormError('')
        setErrors((prev: FieldErrors) => {
            const nextErrors = { ...prev }
            clearCPTError(nextErrors)
            return nextErrors
        })
        setForm((prev: ApiDTO_NTSPform) => ({
            ...prev,
            NewNote: '',
            fk_CPTCodes: [],
            _providerNoteEditMode: false,
            _editedProviderNotesPayload: undefined, //FP-14292 - clear any staged edits when cancelling
        }))
    }

    const applyProviderNoteEditToForm = (sourceForm: ApiDTO_NTSPform): ApiDTO_NTSPform => {
        if (!providerNoteEditFeatureEnabled) {
            return sourceForm
        }

        if (editingProviderNoteId === null || !editingProviderNoteCaseId) {
            return sourceForm
        }

        const updatedText = (sourceForm.NewNote || '').trim()
        if (!updatedText) {
            return sourceForm
        }

        const editableCPTCodeIds = getUniqueCPTCodeIds(
            Array.isArray(sourceForm.fk_CPTCodes) ? sourceForm.fk_CPTCodes : []
        )

        let didUpdate = false
        let editedGroupPayload: ApiDTO_NTSPform['ProviderNotes'] //FP-14292 will contain only the edited note group with the edited note for the backend update, or be undefined if no edits were made
        const groups = Array.isArray(sourceForm.ProviderNotes) ? sourceForm.ProviderNotes : []
        const updatedProviderNotes = groups.map((group) => {
            if (group.CaseId !== editingProviderNoteCaseId) {
                return group
            }

            return {
                ...group,
                ProviderCaseNotes: group.ProviderCaseNotes.map((note) => {
                    if (note.Id !== editingProviderNoteId) {
                        return note
                    }

                    if (!isEditableByCurrentUser(note, currentUserId)) {
                        return note
                    }

                    if (!isNoteWithinEditWindow(note.ts_CreationDateTime)) {
                        return note
                    }

                    didUpdate = true
                    const updatedNote = {
                        ...note,
                        Note: updatedText,
                        fk_CPTCodes: editableCPTCodeIds,
                        UpdatedByUserId: currentUserId,
                    }
                    return updatedNote
                }),
            }
            
        })
        //FP-14292 If we found the note being edited and it was updated, prepare the editedGroupPayload to contain only the edited note for the backend update. This allows us to avoid sending the entire set of provider notes which may have other notes that were added/edited by the provider during this edit session that we don't want to overwrite.
        const editedGroup = updatedProviderNotes.find(
            (group) => group.CaseId === editingProviderNoteCaseId
        )
        if (editedGroup) {
            const editedNote = editedGroup.ProviderCaseNotes.find(
                (note) => note.Id === editingProviderNoteId
            )
            if (editedNote) {
                editedGroupPayload = [
                    {
                        ...editedGroup,
                        ProviderCaseNotes: [editedNote],
                    },
                ]
            }
        }

        if (!didUpdate) {
            return sourceForm
        }

        return {
            ...sourceForm,
            ProviderNotes: updatedProviderNotes,
            _providerNotesEdited: true,
            _providerNoteEditMode: true,
            _editedProviderNotesPayload: editedGroupPayload, //FP-14292 payload containing only the edited note group with the edited note for backend update
        }
    }

    const isEditingProviderNote =
        editingProviderNoteId !== null && editingProviderNoteCaseId.length > 0

    // useEffect(() => {
    //     const handleTabKeyPress = (event: {
    //         key: string
    //         shiftKey: any
    //         preventDefault: () => void
    //     }) => {
    //         if (event.key === 'Tab') {
    //             if (event.shiftKey) {
    //                 // Shift + Tab (backward)
    //                 if (document.activeElement === firstInputRef.current) {
    //                     event.preventDefault()
    //                     lastInputRef.current?.focus()
    //                 }
    //             } else {
    //                 // Tab (forward)
    //                 if (document.activeElement === lastInputRef.current) {
    //                     event.preventDefault()
    //                     firstInputRef.current?.focus()
    //                 }
    //             }
    //         }
    //     }

    //     const formElement = formRef.current
    //     if (formElement) {
    //         formElement.addEventListener('keydown', handleTabKeyPress)
    //     }

    //     return () => {
    //         if (formElement) {
    //             formElement.removeEventListener('keydown', handleTabKeyPress)
    //         }
    //     }
    // }, [])

//     const uniqueProviderNotes = React.useMemo(() => {
//     const seen = new Set();
//     return providerNotes?.filter(noteGroup => {
//         const key = `${noteGroup.CaseId}_${noteGroup.fk_FollowUp}`;
//         if (seen.has(key)) return false;
//         seen.add(key);
//         return true;
//     }) || [];
// }, [providerNotes]);

    const handleChange = (e: any) => {
        const { name, value, type }: { name: string; value: any; type: string } = e.target
        const tempErrors: FieldErrors = { ...errors }
        const schema = validationSchema[name]
        // NOTE: this is very fragile and we should revisit. If there is any scenario where an error 
        // relies on a value that does not match the value returned from e.target it will not show the correct error
        // (for example you have to look at the target.checked property for a checkbox input not its value)
        const tempValue = name === "fk_FollowupNeeded" ? Number(!(e.target as HTMLInputElement).checked) : value;
        tempErrors[name] = typeof schema === 'function' ? schema(buildValidationForm(form, { [name]: tempValue }), NTSPElements) : schema || ''

        if (name === 'NewNote') {
            setRemainingNotes(2048 - e.currentTarget.value.length)
            // Update form state for NewNote
            setForm((prev: ApiDTO_NTSPform) => ({
                ...prev,
                NewNote: value,
            }))
        } else if (name === 'fk_ConsultService') {
            setForm((prev: ApiDTO_NTSPform) => ({
                ...prev,
                [name]: Number(value),
                fk_FollowUps: [-1],
            }))
            return
        } else if (name.includes('fk_FollowUp')) {
            const followUpSet = new Set(
                typeof form.fk_FollowUps !== 'number' ? form.fk_FollowUps : []
            )
            const followUpId = Number(name.split('_')[2])
            const is24HourToggle = is24HourFollowUp(NTSPElements, followUpId) //FP-14499 - determine if this follow-up being toggled is a 24-hour follow-up to decide whether to store/clear 24-hour related values in the ref
            const isChecked = (e.target as HTMLInputElement).checked
            const was24HourSelectedBefore = Array.from(followUpSet).some(id => is24HourFollowUp(NTSPElements, id))

            if (isChecked) { //FP-14499 - when checking a follow-up, if it's a 24-hour follow-up, store current consultant and handoff values in ref before they potentially get cleared by the logic below when enforcing 24-hour follow-up requirements
                if (is24HourToggle && !was24HourSelectedBefore) {
                    followUp24HourValuesRef.current = {
                        fk_followUpConsultant: form.fk_followUpConsultant,
                        ts_FollowUpHandoff: form.ts_FollowUpHandoff,
                    }
                }
                followUpSet.add(followUpId)
                followUpSet.delete(-1)
            } else {
                followUpSet.delete(followUpId)
                if (followUpSet.size === 0) followUpSet.add(-1)
            }
            //FP-14523 - compute if there are any follow-ups selected (other than the "No Follow Up Needed" option) to determine if follow-up related fields should be enabled or not
            const followUpsArray = Array.from(followUpSet)
            const hasFollowUps = followUpSet.size > 0 && !followUpSet.has(-1)

            const is24HourSelectedAfter = Array.from(followUpSet).some(id => is24HourFollowUp(NTSPElements, id))
            //FP-14499: If 24-hour follow-up was unchecked, clear consultant and handoff fields and related errors
            if (!is24HourSelectedAfter) {
                tempErrors['fk_followUpConsultant'] = ''
                tempErrors['ts_FollowUpHandoff'] = ''
            } else if (submitted) {
                const followUpRequiredErrors = get24HourFollowUpRequiredErrors({
                    ...form,
                    fk_FollowUps: followUpsArray, //FP-14523 - validate based on the new follow-up selection
                } as ApiDTO_NTSPform)
                tempErrors['fk_followUpConsultant'] = followUpRequiredErrors.fk_followUpConsultant || ''
                tempErrors['ts_FollowUpHandoff'] = followUpRequiredErrors.ts_FollowUpHandoff || ''
            }

            // Reset Patient Education if that specific follow-up unchecked (existing logic)
            if (isPatientEducationFollowUp(NTSPElements, followUpId) && !followUpSet.has(followUpId)) {
                setForm(prev => ({
                    ...prev,
                    fk_FollowUps: followUpsArray,//FP-14523 - update follow-up selection in form state
                    fk_FollowupNeeded: hasFollowUps ? 1 : prev.fk_FollowupNeeded,
                }))
                return
            }
            //FP-14499: If 24-hour follow-up just got selected, enforce that consultant and handoff fields are filled out by setting errors for those fields (which will prevent form submission until user fills them out) and if user had previously entered values for those fields before toggling the 24-hour follow-up off, restore those values to avoid making user re-enter them
            if (is24HourToggle && isChecked) {
                setForm(prev => ({
                    ...prev,
                    fk_FollowUps: followUpsArray,
                    fk_FollowupNeeded: hasFollowUps ? 1 : prev.fk_FollowupNeeded,
                    fk_followUpConsultant: -1,
                    ts_FollowUpHandoff: null,
                }))
                tempErrors["fk_FollowupNeeded"] = typeof schema === 'function' ? schema(buildValidationForm(form, { fk_FollowUps: followUpsArray }), NTSPElements) : schema || ''
                return
            }

            // Clear second-column fields if 24-hour follow-up no longer selected
            if (was24HourSelectedBefore && !is24HourSelectedAfter) {
                const saved24HourValues = followUp24HourValuesRef.current //FP-14499 - retrieve any previously saved 24-hour follow-up related values from the ref to restore them when unchecking the 24-hour follow-up (since those fields are required when 24-hour follow-up is selected but not when it's unselected, we want to avoid making user re-enter values for those fields if they toggle 24-hour follow-up on then off again)
                followUp24HourValuesRef.current = null
                setForm(prev => ({
                    ...prev,
                    fk_FollowUps: followUpsArray,//FP-14523 - update follow-up selection in form state
                    fk_FollowupNeeded: hasFollowUps ? 1 : prev.fk_FollowupNeeded,
                    followUpCategory: '',
                    ts_FollowUpSchedule: null,
                    fk_followUpConsultant: is24HourToggle && saved24HourValues ? saved24HourValues.fk_followUpConsultant : prev.fk_followUpConsultant,
                    ts_FollowUpHandoff: is24HourToggle && saved24HourValues ? saved24HourValues.ts_FollowUpHandoff : prev.ts_FollowUpHandoff,//FP-14499 - restore any previously saved values for consultant and handoff when unchecking 24-hour follow-up
                }))
                return
            }

            setForm(prev => ({
                ...prev,
                fk_FollowUps: followUpsArray,//FP-14523 - update follow-up selection in form state
                fk_FollowupNeeded: hasFollowUps ? 1 : prev.fk_FollowupNeeded,
            }))
            tempErrors["fk_FollowupNeeded"] = typeof schema === 'function' ? schema(buildValidationForm(form, { fk_FollowUps: followUpsArray }), NTSPElements) : schema || '' //FP-14523 - validate the "No Follow Up Needed" checkbox based on the new follow-up selection
        } else if (name === 'fk_FollowupNeeded') {
            // Handle "No Follow Up Needed" checkbox
            const isChecked = (e.target as HTMLInputElement).checked;
            if (isChecked) {
                // Clear all followups when "No Follow Up Needed" is checked
                setForm((prevForm) => ({
                    ...prevForm,
                    fk_FollowupNeeded: 0,
                    fk_FollowUps: [-1],
                    ts_FollowUpSchedule: null,
                }))
            } else {
                // Uncheck "No Follow Up Needed"
                setForm((prevForm) => ({
                    ...prevForm,
                    fk_FollowupNeeded: 1,
                }))
            }
        } else if (name === 'ExamCompleted') {
            const isChecked = (e.target as HTMLInputElement).checked;
            setExamCompletedModified(true); // Track that user modified ExamCompleted
            setForm((prev: ApiDTO_NTSPform) => ({
                ...prev,
                ExamCompleted: isChecked,
            }))
            if (isChecked && (!form.NewNote || form.NewNote.trim() === '')) {
                tempErrors['NewNote'] = 'New Provider Note is required when Exam Completed is selected.'
            } else {
                const newNoteSchema = validationSchema['NewNote']
                tempErrors['NewNote'] = newNoteSchema && typeof newNoteSchema === 'function'
                    ? newNoteSchema({ ...form, ExamCompleted: isChecked }, NTSPElements)
                    : ''
            }
        } else if (name === 'fk_CPTCode') {
            // When fk_CPTCode changes, also validate NewNote since it depends on fk_CPTCode
            const cptCodeValue = Number(value);
            setForm((prev: ApiDTO_NTSPform) => ({
                ...prev,
                fk_CPTCode: cptCodeValue,
            }))
            // Validate NewNote field when fk_CPTCode changes
            const newNoteSchema = validationSchema['NewNote'];
            if (newNoteSchema) {
                tempErrors['NewNote'] = typeof newNoteSchema === 'function' 
                    ? newNoteSchema({...form, fk_CPTCode: cptCodeValue }, NTSPElements) 
                    : newNoteSchema || '';
            }
        } else if (name === 'fk_Thrombolytic') {
            const thrombolyticValue = Number(value)
            setForm((prev: ApiDTO_NTSPform) => ({
                ...prev,
                fk_Thrombolytic: thrombolyticValue,
            }))
            const thrombolyticTimeSchema = validationSchema['ts_Thrombolytic']
            if (thrombolyticTimeSchema) {
                tempErrors['ts_Thrombolytic'] = typeof thrombolyticTimeSchema === 'function'
                    ? thrombolyticTimeSchema({ ...form, fk_Thrombolytic: thrombolyticValue }, NTSPElements)
                    : thrombolyticTimeSchema || ''
            }
        } else if (name === 'fk_Outcome') {
            const outcomeValue = Number(value);
            setForm((prev: ApiDTO_NTSPform) => ({
                ...prev,
                fk_Outcome: outcomeValue,
            }))
            const nextForm = { ...form, fk_Outcome: outcomeValue }
            const cptIsRequired =
                isCPTMandatory(nextForm, NTSPElements, wasCompletedOnLoad, providerNotes, currentUserId) &&
                hasNoValidCPTCodes(nextForm.fk_CPTCodes)

            setIsMandatory(
                isCPTMandatory(nextForm, NTSPElements, wasCompletedOnLoad, providerNotes, currentUserId)
            )

            if (cptIsRequired) {
                tempErrors['fk_CPTCode'] = 'At least one CPT Code is required.';
                tempErrors['fk_CPTCodes'] = 'CPT Code is required.';
            } else {
                tempErrors['fk_CPTCodes'] = '';
                tempErrors['fk_CPTCode'] = '';
            }
        } else {
            setForm((prev: ApiDTO_NTSPform) => ({
                ...prev,
                [name]:
                    type === 'select-one'
                        ? Number(value)
                        : name === 'fk_Contact' || name === 'fk_HandOffCriteria' 
                          ? Number(value)
                          : name === "fk_FollowupNeeded" 
                            ? Number(!(e.target as HTMLInputElement).checked)
                            : type === 'checkbox'
                                ? (e.target as HTMLInputElement).checked
                                : type === 'select-one'
                                ? Number(value)
                                : value,
            }))
            //FP-14499: If user modifies follow-up consultant or handoff fields, validate them if any 24-hour follow-up is selected
            if (name === 'fk_followUpConsultant' && isAny24HourFollowUpSelected) {
                tempErrors['fk_followUpConsultant'] = Number(value) === -1
                    ? 'Consultant is required for 24-hour follow-up.'
                    : ''
            }

            if (name === 'ts_FollowUpHandoff' && isAny24HourFollowUpSelected) {
                tempErrors['ts_FollowUpHandoff'] = value
                    ? ''
                    : 'Hand Off date and time is required for 24-hour follow-up.'
            }
        }

        setErrors(tempErrors)

        if (
            submitted &&
            Object.values(tempErrors) &&
            Object.values(tempErrors).join('').length > 0
        ) {
            setFormError('Please fix the errors before saving.')
            return
        } else {
            setFormError('')
        }
    }

    const handleBlur = (
        e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target
        if (name === 'fk_followUpConsultant' || name === 'ts_FollowUpHandoff') {
            const followUpRequiredErrors = get24HourFollowUpRequiredErrors({
                ...form,
                [name]: name === 'fk_followUpConsultant' ? Number(value) : value,
            } as ApiDTO_NTSPform)
            setErrors((prev: { [s: string]: string }) => ({
                ...prev,
                fk_followUpConsultant: followUpRequiredErrors.fk_followUpConsultant || '',
                ts_FollowUpHandoff: followUpRequiredErrors.ts_FollowUpHandoff || '',
            }))
        }//FP-14499: Validate consultant and handoff fields on blur if they are relevant for 24-hour follow-up
        if (validationSchema[name]) {
            const updatedForm = buildValidationForm(form, { [name]: value })
            setErrors((prev: { [s: string]: string }) => ({
                ...prev,
                [name]: validationSchema[name](updatedForm, NTSPElements),
            }))
        }
    }

    // Handler for date field validation errors from DatePicker
    const handleDateValidationError = (fieldName: string) => (error: string) => {
        setErrors((prev: { [s: string]: string }) => ({
            ...prev,
            [fieldName]: error,
        }))
    }

    useEffect(() => {
    setSelectedCaseId(form.CaseId);
    setExamCompletedModified(false); // Reset the flag when case changes
}, [form.CaseId]);

    //FP-14227 Single config for service lines and enablement rules
    const SERVICE_LINE_CONFIG = [
        {
            key: 'ACUTE',
            label: 'Acute',
            enable: (current: string, isSubacuteAllowed: boolean) => current === 'Acute',
        },
        {
            key: 'SUBACUTE',
            label: 'Subacute',
            enable: (current: string, isSubacuteAllowed: boolean) => current === 'Subacute' && isSubacuteAllowed,
        },
        {
            key: 'TELE_EEG',
            label: 'TeleEEG',
            enable: (current: string, isSubacuteAllowed: boolean) => current === 'TeleEEG',
        },
        {
            key: 'NEUROHOSPITALIST',
            label: 'Neurohospitalist',
            enable: (current: string, isSubacuteAllowed: boolean) => current === 'Neurohospitalist',
        },
    ];

    //FP-14227 Generate SERVICE_LINES and followUpEnablementRules from config
    const SERVICE_LINES = Object.fromEntries(SERVICE_LINE_CONFIG.map(s => [s.key, s.label]));
    const followUpEnablementRules = Object.fromEntries(
        SERVICE_LINE_CONFIG.map(s => [s.label, s.enable])
    );
    //FP-14227 Refactor getUniqueFollowUps to use new config and DRY enablement logic
    function getCurrentServiceLine(serviceKey: number) {
        switch (serviceKey) {
            case AcuteServiceKey:
                return SERVICE_LINES.ACUTE;
            case SabacuteServiceKey:
                return SERVICE_LINES.SUBACUTE;
            case teleEEGServiceKey:
                return SERVICE_LINES.TELE_EEG;
            case Neurohospitalistkey:
                return SERVICE_LINES.NEUROHOSPITALIST;
            default:
                return '';
        }
    }
    //FP-14227 Add helper to check subacute enablement based on facility
    function isSubacuteAllowedForFacility(facilities: any[], facilityKey: number) {
        return facilities?.some(
            (facility) =>
                facility._key === facilityKey &&
                facility?.SiteStatus?.toLowerCase() === 'acute and subacute' //FP-14539
        );
    }
       //FP-14227 Add helper to check neurohospitalist enable based on facility
    function isNeurohospitalistAllowedForFacility(facilities: any[], facilityKey: number) {
        return facilities?.some(
            (facility) =>
                facility._key === facilityKey &&
                facility.IsNeurohospitalist==true
        );
    }
    //FP-14227 
    const getUniqueFollowUps = () => {
        const { FollowUp, ConsultServices, Facilities } = NTSPElements || {};
        if (!FollowUp || !ConsultServices) return [];

        const currentServiceLine = getCurrentServiceLine(form.fk_ConsultService); //FP-14227 Determine current service line based on form selection
        const isSubacuteAllowed = isSubacuteAllowedForFacility(Facilities ?? [], form.fk_Facility); //FP-14227 Check if subacute is allowed for current facility
        const isNeurohospitalistAllowed = isNeurohospitalistAllowedForFacility(Facilities ?? [], form.fk_Facility); //FP-14227 Check if neurohospitalist is allowed for current facility
        const currentFollowUp =form.fk_FollowUp!=-1?
         NTSPElements?.FollowUp?.find((f: NTSPElement) => f._key === form.fk_FollowUp)?.
         ETLMatchValue:"Unknown"; //FP-14227 Get current follow-up element to determine if it's 24-hour for default selection logic
        //FP-14227 Group by FriendlyName, prefer current service line
        const followUpsByName = new Map();
        for (const followUp of FollowUp) {
            const existing = followUpsByName.get(followUp.FriendlyName);
            if (
                !existing ||
                (followUp.ServiceLine === currentServiceLine &&
                    existing.ServiceLine !== currentServiceLine)
            ) {
                followUpsByName.set(followUp.FriendlyName, followUp)
            }
        }

        //FP-14227 Use DRY enablement logic from config
        function isFollowUpEnabled(
            followUp: { ServiceLine: string; [key: string]: any },
            currentServiceLine: string,
            isSubacuteAllowed: boolean,
            isNeurohospitalistAllowed: boolean
        ) {
            //FP-14393 - "Subacute Follow Up Needed" should only show when the facility supports Subacute (need to revisit displaying of follow-ups.)
            const isExplicitSubacuteFollowUp =
                (followUp.ETLMatchValue || '').toString().toLowerCase().includes('subacute follow up needed') ||
                (followUp.FriendlyName || '').toString().toLowerCase().includes('subacute follow up needed')
            if (isExplicitSubacuteFollowUp && !isSubacuteAllowed) {
                return false;
            }

            //FP-14521: Patient Education should only show when the facility supports Subacute.
            const isPatientEducation = isPatientEducationFollowUp(NTSPElements, followUp._key)
            if (isPatientEducation && !isSubacuteAllowed) {
                return false;
            }


            //FP-14227 Require isNeurohospitalistAllowed for Neurohospitalist
            if (
                [SERVICE_LINES.NEUROHOSPITALIST].includes(followUp.FollowUpServiceLine) &&
                !isNeurohospitalistAllowed
            ) {
                return false;
            }

            const rule = followUpEnablementRules[followUp.ServiceLine];
            return rule ? (rule(currentServiceLine, isSubacuteAllowed) 
            && followUp.ETLMatchValue
             != currentFollowUp) : false;
        }

        return Array.from(followUpsByName.values())
            .map((followUp) => ({
                ...followUp,
                isEnabled: isFollowUpEnabled(followUp, currentServiceLine, isSubacuteAllowed, isNeurohospitalistAllowed), //FP-14227 Determine enablement using new helper function and config rules
            }))
            .sort((a, b) => {
                if (a.ServiceLine === currentServiceLine && b.ServiceLine !== currentServiceLine) return -1;
                if (a.ServiceLine !== currentServiceLine && b.ServiceLine === currentServiceLine) return 1;
                return a.DisplayOrder - b.DisplayOrder;
            });
    }
    const isAny24HourFollowUpSelected =
        typeof form.fk_FollowUps !== 'number' &&
        Array.isArray(form.fk_FollowUps) &&
        form.fk_FollowUps.some(id => is24HourFollowUp(NTSPElements, id));
    
    // Check if site supports Acute and SubAcute
    const isSiteAcuteAndSubAcute = form.fk_Facility !== -1 && 
        NTSPElements?.Facilities?.some(
            (facility) =>
                facility._key === form.fk_Facility &&
                facility?.SiteStatus?.toLowerCase() === 'acute and subacute' //FP-14539
        );

    const checkCPTcode = () => {
        const formForValidation = applyProviderNoteEditToForm(form)
        const providerNotesForMandatoryCheck = getProviderNotesForMandatoryCheck(formForValidation)
        if(isCPTMandatory(formForValidation, NTSPElements, wasCompletedOnLoad, providerNotesForMandatoryCheck, currentUserId) && (!formForValidation.fk_CPTCodes || !Array.isArray(formForValidation.fk_CPTCodes) || formForValidation.fk_CPTCodes.length === 0 || formForValidation.fk_CPTCodes.every((code: number) => code === -1))){
             setErrors((prev: FieldErrors) => ({ 
                ...prev,
                fk_CPTCode: 'At least one CPT Code is required.',
                fk_CPTCodes: 'At least one CPT Code is required.',
            }))
        } else {
            setErrors((prev: FieldErrors) => ({ 
                ...prev,
                fk_CPTCode: '',
                fk_CPTCodes: '',
            }))
        }
    }
    
    
    return (
        <>
        <div className='sticky-Patient-header'>
            <PatientDetailsHeader
                form={form}
                NTSPElements={NTSPElements}
            />
            </div>
            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    const formForValidation = applyProviderNoteEditToForm(form)
                    const providerNotesForMandatoryCheck = getProviderNotesForMandatoryCheck(formForValidation)
                    if(isCPTMandatory(formForValidation, NTSPElements, wasCompletedOnLoad, providerNotesForMandatoryCheck, currentUserId) && hasNoValidCPTCodes(formForValidation.fk_CPTCodes)){ //FP-14522 Check CPT code requirement on save
                        setFormError('Please fix the errors before saving.')
                        setErrors((prev: FieldErrors) => ({
                            ...prev,
                            fk_CPTCode: 'At least one CPT Code is required.',
                            fk_CPTCodes: 'At least one CPT Code is required.',//FP-14522
                        }))
                        return
                    }
                    wrapHandleSave()
                }}
                className="ntsp-form-container"
                ref={formRef}
            >
                {/* Row 1 */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '2rem',
                        flexWrap: 'wrap',
                    }}
                >
                    {/* Column 1 */}
                    <div className="patient-details-clinicalnotes-block">
                        <div className="form-group">
                            <label className="mb-0" htmlFor="fk_Diagnosis">
                                Diagnosis:
                            </label>
                            <span>
                                <select
                                    onChange={handleChange}
                                    className="ntsp-fields"
                                    id="fk_Diagnosis"
                                    value={form.fk_Diagnosis}
                                    name="fk_Diagnosis"
                                    tabIndex={1}
                                    ref={firstInputRef}
                                >
                                    <option value="-1">-Select Diagnosis-</option>
                                    {teleEEGServiceKey == form.fk_ConsultService &&
                                    NTSPElements?.Diagnosis?.map((Diagnosis: NTSPElement) => (
                                         Diagnosis.IsEEG === true && (
                                            <option key={Diagnosis._key} value={Diagnosis._key}>
                                                {Diagnosis.FriendlyName}
                                            </option>
                                        ) 
                                    ))}
                                    {teleEEGServiceKey !== form.fk_ConsultService &&
                                    NTSPElements?.Diagnosis?.map((Diagnosis: NTSPElement) => (
                                        Diagnosis.IsEEG === false && (
                                            <option key={Diagnosis._key} value={Diagnosis._key}>
                                                {Diagnosis.FriendlyName}
                                            </option>
                                        ) ))}
                                </select>
                            </span>
                        </div>
                        {AcuteServiceKey === form.fk_ConsultService && //FP-14500 Thrombolytic should only show for Acute non-EEG consults
                        <div className="form-group">
                            <label htmlFor="Thrombolytic">Thrombolytic Given:</label>
                            <span>
                                <select
                                    className="ntsp-fields"
                                    id="Thrombolytic"
                                    name="fk_Thrombolytic"
                                    value={form.fk_Thrombolytic}
                                    onChange={handleChange}
                                    tabIndex={3}
                                >
                                    <option value="-1">-Select Thrombolytic-</option>
                                    {NTSPElements?.Thrombolytic?.map(
                                        (Thrombolytic: NTSPElement) => (
                                            <option
                                                key={Thrombolytic._key}
                                                value={Thrombolytic._key}
                                            >
                                                {Thrombolytic.FriendlyName}
                                            </option>
                                        )
                                    )}
                                </select>
                            </span>
                        </div>
                        } 
                        {teleEEGServiceKey === form.fk_ConsultService &&
                         <div className="form-group">
                            <label htmlFor="ASD">ASD given:</label>
                            <span>
                                <select
                                    className="ntsp-fields"
                                    id="ASD"
                                    name="fk_ASD"
                                    value={form.fk_ASD}
                                    onChange={handleChange}
                                    tabIndex={3}
                                >
                                    <option value="-1">-Select ASD-</option>
                                    {NTSPElements?.ASDGiven?.map(
                                        (ASDGiven: NTSPElement) => (
                                            <option
                                                key={ASDGiven._key}
                                                value={ASDGiven._key}
                                            >
                                                {ASDGiven.FriendlyName}
                                            </option>
                                        )
                                    )}
                                </select>
                            </span>
                        </div>
                        }
                        <div className="form-group">
                            <label htmlFor="fk_Outcome">Status/Type:</label>
                            <span>
                                <select
                                    className="ntsp-fields"
                                    id="fk_Outcome"
                                    name="fk_Outcome"
                                    value={form.fk_Outcome}
                                    onChange={handleChange}
                                    tabIndex={6}
                                >
                                    <option value="-1">-Select Status-</option>
                                    {NTSPElements?.Outcome?.map((Status: NTSPElement) => (
                                        <option key={Status._key} value={Status._key}>
                                            {Status.FriendlyName}
                                        </option>
                                    ))}
                                </select>
                            </span>
                        </div>
                        {/* First textarea - Highlights & Plan in column 1 */}
                            <div style={{ marginTop: getAssessmentPlanMarginTop(form.fk_ConsultService) }}> {/*FP-14500 - add margin top based on consult service */}
                            <label htmlFor="HighlightsPlan" className={
                                        !isSiteAcuteAndSubAcute && form.fk_ConsultService !== SabacuteServiceKey
                                            ? 'disabled'
                                            : ''
                                    + ' mb-1'}>
                            Assessment/Plan: {/*FP-14502 - Change label from "Highlights & Plan" to "Assessment/Plan" */}
                            </label>
                                <textarea
                                id="HighlightsPlan"
                                className="w-100 mt-2"
                                style={{
                                    height: '155px',
                                    overflowWrap: 'break-word',
                                    cursor: !isSiteAcuteAndSubAcute && form.fk_ConsultService !== SabacuteServiceKey && form.fk_ConsultService!==Neurohospitalistkey ? 'not-allowed' : 'text',
                                }}
                                maxLength={2048}
                                disabled={!isSiteAcuteAndSubAcute && form.fk_ConsultService !== SabacuteServiceKey && form.fk_ConsultService!==Neurohospitalistkey}
                                title={!isSiteAcuteAndSubAcute && form.fk_ConsultService !== SabacuteServiceKey && form.fk_ConsultService!==Neurohospitalistkey ? "Disabled because the consult service is not Subacute or Neurohospitalist and site does not support Acute and SubAcute" : ""}
                                placeholder="Enter assessment and plan" //FP-14502 placeholder text updated as per SME request
                                    value={form.HighlightsPlan || ''}
                                    onChange={handleChange}
                                name='HighlightsPlan'
                                tabIndex={11}
                            >{form.HighlightsPlan}
                            </textarea>
                        </div>
                    </div>
                    {/* Column 2 */}
                    <div className="nurse-notes-first-block">
                        <div className="form-group">
                            <label htmlFor="flag_DiscussedConference">
                                Discussed At Conference:
                            </label>
                            <span>
                                <input
                                    className="ntsp-fields"
                                    type="checkbox"
                                    name="flag_DiscussedConference"
                                    checked={form.flag_DiscussedConference}
                                    onChange={handleChange}
                                    id="flag_DiscussedConference"
                                    tabIndex={2}
                                />
                            </span>
                        </div>
                        {form.fk_ConsultService === AcuteServiceKey && (
                        <div className="form-group">
                            <label htmlFor="ts_Thrombolytic">
                            Thrombolytic given time
                            {form.fk_Thrombolytic !== -1 && (
                                <span>*</span>
                            )}:
                            </label>
                            <span style={{ display: 'flex', flexDirection: 'column', position:'relative', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <DatePicker
                                    id="ts_Thrombolytic"
                                    name="ts_Thrombolytic"
                                    dateFormat="MM/dd/yyyy HH:mm"
                                    inputClass="ntsp-fields"
                                    timeformat="HH:mm"
                                    showMasking={true}
                                    showTime={true}
                                    startingDate={form.ts_Thrombolytic ? new Date(form.ts_Thrombolytic) : null}
                                    setStartingDate={(dateString) => {
                                        const event = {
                                            target: {
                                                name: 'ts_Thrombolytic',
                                                value: dateString,
                                                type: 'text',
                                            },
                                        }
                                        handleChange(event)
                                        if (handleBlur) {
                                            handleBlur(event as any)
                                        }
                                    }}
                                    placeholder="Select Date & Time"
                                    style={{ width: '155px' }}
                                    isClearable={true}
                                    calendarClosed={true}
                                    tabIndex={4}
                                    onValidationError={handleDateValidationError('ts_Thrombolytic')}
                                />
                                    {/* <span className="mx-1" style={{ lineHeight: "2.1", fontSize: "12px" }}>{IANAToTzCode(timezone)}</span> */}
                                    <button
                                        type={'button'}
                                        className="d-inline-block app-button"
                                        onClick={() => {
                                            const selectedFacility = NTSPElements?.Facilities?.find(
                                                (f) => f._key === form.fk_Facility
                                            )
                                            const ianaTz = selectedFacility?.TZCode
                                                ? tzCodeToIANA(selectedFacility.TZCode)
                                                : undefined
                                            const now = ianaTz
                                                ? moment.tz(new Date(), ianaTz)
                                                : moment(new Date())
                                            setForm((prev: ApiDTO_NTSPform) => ({
                                                ...prev,
                                                ts_Thrombolytic: now.format('YYYY-MM-DDTHH:mm'),
                                            }))
                                        }}
                                        tabIndex={5}
                                    >
                                        Now
                                    </button>
                                    </div>
                                {errors.ts_Thrombolytic && (
                                    <div
                                        style={{
                                              color: 'red',
                                            position: 'absolute',
                                            lineHeight: '1',
                                            fontSize: "smaller",
                                            margin: '3px',
                                            marginTop: '35px'
                                        }}
                                    >
                                        {errors.ts_Thrombolytic + ''}
                                    </div>
                                )}
                            </span>
                        </div>
                        )}
                        <div className="form-group">
                            <label htmlFor="flag_InterestingCase" style={{marginRight: '12px'}}>Interesting Case:</label>
                            <span>
                                <input
                                    className="ntsp-fields"
                                    type="checkbox"
                                    name="flag_InterestingCase"
                                    checked={form.flag_InterestingCase}
                                    onChange={handleChange}
                                    id="flag_InterestingCase"
                                    tabIndex={form.fk_ConsultService === AcuteServiceKey ? 7:4}
                                />
                            </span>
                        </div>
                        <div className="form-group">
                            <label>Customer/Patient Contact:</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', lineHeight: '1', marginTop:'3px' }}>
                                {NTSPElements?.Contact?.map((pos: NTSPElement, index: number) => (
                                    <div key={pos._key}>
                                        <input
                                            type="radio"
                                            name="fk_Contact"
                                            id={`contact_${pos._key}`}
                                            value={pos._key}
                                            checked={form.fk_Contact === pos._key}
                                            onChange={handleChange}
                                            tabIndex={form.fk_ConsultService === AcuteServiceKey ? 10 : 6}
                                            ref={index === 0 ? firstInputRef : undefined}
                                        />
                                        <label
                                            className="ml-2"
                                            style={{ minWidth: '65px' }}
                                            htmlFor={`callerPosition${pos._key}`}
                                        >
                                            {pos.FriendlyName}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{marginTop: form.fk_ConsultService === SabacuteServiceKey ? '78px' : form.fk_ConsultService === teleEEGServiceKey ? '5px' : form.fk_ConsultService === AcuteServiceKey ?  '0px' : '4px'}}> 
                               <label htmlFor="SiteContactInfo" className={
                                        SabacuteServiceKey !== form.fk_ConsultService
                                            ? 'disabled'
                                            : ''
                                    + ' mb-1'}>
                                    Site Contact Info:
                                </label>
                                <textarea
                                    id="SiteContactInfo"
                                    className="w-100 mt-2"
                                    style={{
                                        height: '155px',
                                        overflowY: 'auto',
                                        overflowWrap: 'break-word',
                                        cursor: form.fk_ConsultService !== SabacuteServiceKey ? 'not-allowed' : 'text',
                                    }}
                                    maxLength={2048}
                                    name="SiteContactInfo"
                                    placeholder="Enter site contact information"
                                    value={form.SiteContactInfo || ''}
                                    onChange={handleChange}
                                    title={form.fk_ConsultService !== SabacuteServiceKey ? "Disabled because the consult service is not Subacute" : ""}
                                    disabled={form.fk_ConsultService !== SabacuteServiceKey}
                                    tabIndex={12}
                                />
                            </div>
                        

                        {/* Second textarea - Only Followup Notes in column 2 */}
                        {/* {form.fk_ConsultService === SabacuteServiceKey && (
                            <div className="form-group" style={{ marginTop: 87 }}>
                                <label htmlFor="FollowupNotes" className="mb-1">
                                    Followup Notes:
                                </label>
                                <textarea
                                    id="FollowupNotes"
                                    className="w-100 mt-2"
                                    style={{
                                        height: '6rem',
                                        overflowY: 'auto',
                                        resize: 'vertical',
                                        minHeight: '6rem',
                                        maxHeight: '12rem',
                                    }}
                                    name="FollowupNotes"
                                    placeholder="Enter followup notes"
                                    value={form.FollowupNotes || ''}
                                    onChange={handleChange}
                                />
                            </div>
                        )} */}
                    </div>
                    {/* Column 3 */}
                    <div className="nurse-notes-first-block">
                        <div className="form-group">
                            <label className="">Video Conferencing:</label>
                            <span>
                                <ul className="list-group">
                                    <div
                                        className="ntsp-video"
                                        style={{
                                            minWidth: '200px',
                                            height: '83px',
                                            overflowY: 'scroll',
                                        }}
                                    >
                                        {NTSPElements?.Facilities?.filter(
                                            (location: NTSPElement) =>
                                                location._key === form.fk_Facility
                                        ).flatMap((location: NTSPElement) =>
                                            (location?.LocationDevice ?? []).map(
                                                (device: NTSPDevices) => (
                                                    <li
                                                        key={device._key}
                                                        className="list-group-item py-2"
                                                    >
                                                        <a
                                                            href={device.URL}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {device.DeviceName}
                                                        </a>
                                                    </li>
                                                )
                                            )
                                        )}
                                        {!NTSPElements?.Facilities?.some(
                                            (location: NTSPElement) =>
                                                location._key === form.fk_Facility &&
                                                Array.isArray(location?.LocationDevice) &&
                                                location.LocationDevice.length > 0
                                        ) && (
                                            <div
                                                className="text-muted"
                                                style={{ padding: '8px', textAlign: 'center' }}
                                            >
                                                No video conferencing devices available.
                                            </div>
                                        )}
                                    </div>
                                </ul>
                            </span>
                        </div>
                        <div className="form-group">
                            <label htmlFor="ts_Call">
                                Call Ended: <br />
                                <span style={{ fontSize: '11px', color: '#666' }}>
                                    (Patient Facility Time)
                                </span>
                            </label>
                            <span style={{ display: 'flex', flexDirection: 'column', position:'relative', alignItems: 'flex-start' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <DatePicker
                                        id="ts_Call"
                                        name="ts_Call"
                                        dateFormat="MM/dd/yyyy HH:mm"
                                        //className="ntsp-fields"
                                        inputClass="ntsp-fields"
                                        timeformat="HH:mm"
                                        showMasking={true}
                                        showTime={true}
                                        startingDate={form.ts_Call ? new Date(form.ts_Call) : null}
                                        setStartingDate={(dateString) => {
                                            const event = {
                                                target: {
                                                    name: 'ts_Call',
                                                    value: dateString,
                                                    type: 'text'
                                                }
                                            }
                                            handleChange(event)
                                            if (handleBlur) {
                                                const event = {
                                                    target: {
                                                        name: 'ts_Call',
                                                        value: dateString,
                                                        type: 'text',
                                                    },
                                                }
                                                handleBlur(event as any)
                                            }
                                        }}
                                        interval={15}
                                        placeholder="Select Date & Time"
                                        style={{ width: '155px', display: 'inline-block' }}
                                        isClearable={true}
                                        calendarClosed={true}
                                        tabIndex={8}
                                        onValidationError={handleDateValidationError('ts_Call')}
                                    />
                                    {/* <span className="mx-1" style={{ lineHeight: "2.1", fontSize: "12px" }}>{IANAToTzCode(timezone)}</span> */}
                                    <button
                                        type={'button'}
                                        className="d-inline-block app-button"
                                        onClick={() => {
                                            const selectedFacility = NTSPElements?.Facilities?.find(
                                                (f) => f._key === form.fk_Facility
                                            )
                                            const ianaTz = selectedFacility?.TZCode
                                                ? tzCodeToIANA(selectedFacility.TZCode)
                                                : undefined
                                            const now = ianaTz
                                                ? moment.tz(new Date(), ianaTz)
                                                : moment(new Date())
                                            setForm((prev: ApiDTO_NTSPform) => ({
                                                ...prev,
                                                ts_Call: now.format('YYYY-MM-DDTHH:mm'),
                                            }))
                                        }}
                                        tabIndex={9}
                                    >
                                        Now
                                    </button>
                                </div>
                                {errors.ts_Call && (
                                    <div
                                        style={{
                                            color: 'red',
                                            fontSize: '12px',
                                            marginTop:'4px',
                                            maxWidth: '155px',
                                            // position: 'absolute'
                                        }}
                                    >
                                        {errors.ts_Call + ''}
                                    </div>
                                )}
                            </span>
                        </div>
                        {form.fk_ConsultService === SabacuteServiceKey && (
                            <>
                                <div className="form-group">
                                    <label
                                        htmlFor="flag_DiagnosisDiscussed"
                                        style={{ maxWidth: '9rem' }}
                                    >
                                        Diagnosis discussed with patient:
                                    </label>
                                    <div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                id="flag_DiagnosisDiscussed"
                                                name="flag_DiagnosisDiscussed"
                                                checked={form.flag_DiagnosisDiscussed}
                                                onChange={handleChange}
                                                tabIndex={5 + (NTSPElements?.Contact?.length || 0)}
                                            />
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '0.5rem',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <DatePicker
                                                    key={`education-time-${form.flag_DiagnosisDiscussed}-${educationTimeReset}`}
                                                    id="ts_DiagnosisTime"
                                                    name="ts_DiagnosisTime"
                                                    dateFormat="MM/dd/yyyy HH:mm"
                                                    //className="ntsp-fields"
                                                    inputClass="ntsp-fields"
                                                    timeformat="HH:mm"
                                                    showMasking={true}
                                                    showTime={true}
                                                    startingDate={
                                                        form.ts_DiagnosisTime &&
                                                        form.flag_DiagnosisDiscussed
                                                            ? new Date(form.ts_DiagnosisTime)
                                                            : null
                                                    }
                                                    setStartingDate={(dateString) => {
                                                        const event = {
                                                            target: {
                                                                name: 'ts_DiagnosisTime',
                                                                value: dateString,
                                                                type: 'text',
                                                            },
                                                        }
                                                        handleChange(event)
                                                        if (handleBlur) {
                                                            handleBlur(event as any)
                                                        }
                                                    }}
                                                    interval={15}
                                                    placeholder="Select Date & Time"
                                                    style={{ width: '155px' }}
                                                    disabled={!form.flag_DiagnosisDiscussed}
                                                    title={!form.flag_DiagnosisDiscussed ? "Disabled until 'Diagnosis discussed with patient' is checked" : ""}
                                                    isClearable={true}
                                                    calendarClosed={true}
                                                    tabIndex={6 + (NTSPElements?.Contact?.length || 0)}
                                                    onValidationError={handleDateValidationError('ts_DiagnosisTime')}
                                                />
                                                <button
                                                    type={'button'}
                                                    className="d-inline-block app-button"
                                                    onClick={() => {
                                                        const selectedFacility =
                                                            NTSPElements?.Facilities?.find(
                                                                (f) => f._key === form.fk_Facility
                                                            )
                                                        const ianaTz = selectedFacility?.TZCode
                                                            ? tzCodeToIANA(selectedFacility.TZCode)
                                                            : undefined
                                                        const now = ianaTz
                                                            ? moment.tz(new Date(), ianaTz)
                                                            : moment(new Date())
                                                        setForm((prev: ApiDTO_NTSPform) => ({
                                                            ...prev,
                                                            ts_DiagnosisTime:
                                                                now.format('YYYY-MM-DDTHH:mm'),
                                                        }))
                                                        setEducationTimeReset((prev) => prev + 1)
                                                    }}
                                                    disabled={!form.flag_DiagnosisDiscussed}
                                                    title={!form.flag_DiagnosisDiscussed ? "Disabled until 'Diagnosis discussed with patient' is checked" : ""}
                                                    tabIndex={7 + (NTSPElements?.Contact?.length || 0)}
                                                >
                                                    Now
                                                </button>
                                            </div>
                                        </div>
                                        {errors.ts_DiagnosisTime && (
                                            <div
                                                style={{
                                                    color: 'red',
                                                    fontSize: '12px',
                                                    marginTop:'4px',
                                                    maxWidth: '155px',
                                                    // position: 'absolute'
                                                }}
                                            >
                                                {errors.ts_DiagnosisTime + ''}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                        {/* Third textarea - Site Contact Info moved to column 1 */}
                        
                        <div
                            
                            style={{ marginTop: form.fk_ConsultService === AcuteServiceKey ? '50px' : '' }}
                        >
                            <label>Follow Up Options:</label>
                            {/* NEW 2-COLUMN LAYOUT START */}
                            <div
                                className="followup-columns mt-2 px-3 py-1"
                                style={{
                                    border: '1px solid rgb(153 153 153)',
                                    minHeight: '8rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0,
                                    flexWrap: 'nowrap',
                                    overflowX: 'auto'
                                }}
                            >
                                {form.fk_ConsultService === teleEEGServiceKey &&
                                    getUniqueFollowUps().map((f, idx) => {
                                        const baseTabIndex = form.fk_ConsultService === SabacuteServiceKey
                                            ? (8 + (NTSPElements?.Contact?.length || 0))
                                            : (form.fk_ConsultService === AcuteServiceKey
                                                ? (12 + (NTSPElements?.Contact?.length || 0))
                                                : (8 + (NTSPElements?.Contact?.length || 0)));
                                        if (f.ServiceLine === 'TeleEEG' && f.FollowUpServiceLine === 'TeleTEC') {
                                            return (
                                                <div
                                                    className="form-group"
                                                    key={f._key}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        margin: 0,
                                                        padding: 0
                                                    }}
                                                >
                                                    <label style={{ minWidth: '260px', color: 'inherit', margin: 0, padding: 0, textAlign: 'left' }}>
                                                        {f.FriendlyName}
                                                    </label>
                                                    <input
                                                        type="checkbox"
                                                        id="flag_TEC"
                                                        name='flag_TEC'
                                                        checked={form.flag_TEC}
                                                        onChange={handleChange}
                                                        tabIndex={baseTabIndex + idx}
                                                        style={{ marginLeft: '0.5rem' }}
                                                    />
                                                </div>
                                            );
                                        }
                                        if (f.ServiceLine === 'TeleEEG' && f.FollowUpServiceLine === 'TeleTNC') {
                                            return (
                                                <div
                                                    className="form-group"
                                                    key={f._key}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        margin: 0,
                                                        padding: 0
                                                    }}
                                                >
                                                    <label style={{ minWidth: '260px', color: 'inherit', margin: 0, padding: 0, textAlign: 'left' }}>
                                                        {f.FriendlyName}
                                                    </label>
                                                    <input
                                                        type="checkbox"
                                                        id="flag_TNC"
                                                        name='flag_TNC'
                                                        checked={form.flag_TNC}
                                                        onChange={handleChange}
                                                        tabIndex={baseTabIndex + idx}
                                                        style={{ marginLeft: '0.5rem' }}
                                                    />
                                                </div>
                                            );
                                        }
                                        return null;
                                    })
                                }
                                {form.fk_ConsultService !== teleEEGServiceKey &&
                                    getUniqueFollowUps().filter(f => f.isEnabled).map((f, idx) => {
                                        // Check if "No Follow Up Needed" checkbox would be displayed
                                        const showNoFollowUpCheckbox = Boolean(
                                            NTSPElements?.Outcome?.find((outcome) => outcome._key === form.fk_Outcome && outcome.IsAllowFollowUp===true) &&
                                            form.fk_Facility !== -1 && 
                                            form.fk_ConsultService !== teleEEGServiceKey &&
                                            NTSPElements?.Facilities?.find(
                                                (facility) =>
                                                    facility._key === form.fk_Facility &&
                                                    (facility?.SiteStatus?.toLowerCase() === 'acute and subacute' || facility.IsNeurohospitalist) //FP-14539 
                                            )
                                        );
                                        const shouldDisable = showNoFollowUpCheckbox && form.fk_FollowupNeeded === 0;
                                        
                                        return (
                                        <div
                                            className="form-group"
                                            key={f._key}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                margin: 0,
                                                padding: 0,
                                                opacity: shouldDisable ? 0.5 : 1,
                                                cursor: shouldDisable ? 'not-allowed' : 'default',
                                            }}
                                        >
                                            <label style={{ 
                                                minWidth: '260px', 
                                                color: 'inherit', 
                                                margin: 0, 
                                                padding: 0, 
                                                textAlign: 'left',
                                                cursor: shouldDisable ? 'not-allowed' : 'default',
                                            }}>
                                                {f.FriendlyName}
                                            </label>
                                            <input
                                                type="checkbox"
                                                name={'fk_FollowUp_' + f._key}
                                                checked={
                                                    (form.fk_FollowUps as any) !== -1 &&
                                                    typeof form.fk_FollowUps !== 'number' &&
                                                    form.fk_FollowUps?.includes(f._key)
                                                }
                                                onChange={handleChange}
                                                disabled={shouldDisable}
                                                tabIndex={13}
                                                style={{ 
                                                    marginLeft: '0.5rem',
                                                    cursor: shouldDisable ? 'not-allowed' : 'pointer',
                                                }}
                                            />
                                        </div>
                                    )})
                                }
                            </div>
                                
                            {/* NEW 2-COLUMN LAYOUT END */}
                        </div>
                        <div>
                            {form.fk_Facility !== -1 &&
                                NTSPElements?.Facilities?.find(
                                    (facility) =>
                                        facility._key === form.fk_Facility &&
                                        facility?.SiteStatus?.toLowerCase() === 'acute and subacute' && //FP-14539
                                        form.fk_ConsultService === AcuteServiceKey
                                ) && (
                                    <label className="mb-1" style={{ color: 'red' }}>
                                        This site supports Sub acute. Do you want to add a followup?
                                    </label>
                                )}
                            {NTSPElements?.Outcome?.find((outcome) => outcome._key === form.fk_Outcome && outcome.IsAllowFollowUp==true)
                            &&  form.fk_Facility !== -1 && form.fk_ConsultService !== teleEEGServiceKey && form.fk_FollowUp === -1 && //FP-14557 : Only show "No Follow Up Needed" option if case is parent
                                NTSPElements?.Facilities?.find(
                                    (facility) =>
                                        facility._key === form.fk_Facility &&
                                        facility?.SiteStatus?.toLowerCase() === 'acute and subacute' //FP-14539
                                ) && (
                                    <div style={{
                                        opacity: (form.fk_FollowUps && form.fk_FollowUps.length > 0 && !form.fk_FollowUps.includes(-1)) ? 0.5 : 1,
                                    }}>
                                        <label htmlFor="fk_FollowupNeeded" style={{
                                            marginRight: '12px',
                                            cursor: (form.fk_FollowUps && form.fk_FollowUps.length > 0 && !form.fk_FollowUps.includes(-1)) ? 'not-allowed' : 'default',
                                        }}>No Follow Up Needed</label>
                                        <span>
                                            <input
                                                className="ntsp-fields"
                                                    type="checkbox"
                                                    name="fk_FollowupNeeded"
                                                    checked={form.fk_FollowupNeeded === 0}
                                                    onChange={handleChange}
                                                    disabled={form.fk_FollowUps && form.fk_FollowUps.length > 0 && !form.fk_FollowUps.includes(-1)}
                                                    id="fk_FollowupNeeded"
                                                    tabIndex={34}
                                                    title={
                                                        (form.fk_FollowUps && form.fk_FollowUps.length > 0 && !form.fk_FollowUps.includes(-1))
                                                            ? "Disabled when follow-up option is selected"
                                                            : "Select if this case does not need a follow up"
                                                    }
                                                    style={{
                                                        cursor: (form.fk_FollowUps && form.fk_FollowUps.length > 0 && !form.fk_FollowUps.includes(-1)) ? 'not-allowed' : 'pointer',
                                                    }}
                                                />
                                            </span>
                                            {errors.fk_FollowupNeeded && (
                                                <div
                                                    style={{
                                                        color: 'red',
                                                        fontSize: '12px',
                                                        marginTop:'4px',
                                                        maxWidth: '351px',
                                                        minWidth: '200px'
                                                        // position: 'absolute'
                                                    }}
                                                >
                                                    {errors.fk_FollowupNeeded + ''}
                                                </div>
                                            )}
                                        </div>
                                )}
                            
                        </div>
                    </div>
                </div>
                <div>
                    {isAny24HourFollowUpSelected && (
                        <div style={{ display:'flex', flexDirection: 'row', justifyContent: 'flex-end', minWidth: '200px', marginRight: '80px' }}>
                            <span className="form-group" style={{ margin: '10px' }}>
                                <label style={{ minWidth: '80px' }} htmlFor="followUpConsultant">Consultant*:</label>{/*FP-14499: Add asterisk to indicate required field when any 24-hour follow-up is selected*/}
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: '0.5rem'
                                    }}
                                >
                                    <select
                                        id="fk_followUpConsultant"
                                        name="fk_followUpConsultant"
                                        className="ntsp-fields"
                                        value={form.fk_followUpConsultant || -1}
                                        onChange={handleChange}
                                        onBlur={handleBlur}//FP-14499: Validate on blur as well to show errors as soon as user interacts with the field, not just on save
                                        tabIndex={30}
                                        style={{ minWidth: '155px' }}
                                    >
                                        <option value="-1">-Select Consultant-</option>
                                        {NTSPElements?.Consultants?.map((Consultant: NTSPElement) => {
                                            const is24Hr = consultants24HrSet.has(Consultant._key)
                                            return (
                                                <option
                                                    key={Consultant._key}
                                                    value={Consultant._key}
                                                    style={is24Hr ? {
                                                        backgroundColor: '#2ca348ff',
                                                        fontWeight: 600
                                                    } : undefined}
                                                >
                                                    {Consultant.FriendlyName}                                                               
                                                </option>
                                            )
                                        })}
                                    </select>
                                    {/* Optional legend */}
                                    {(NTSPElements?.Consultants24Hr?.length ?? 0) > 0 && (
                                        <small style={{ color: '#2e7d32', fontSize: '11px' }}>
                                            Green = Available consultant
                                        </small>
                                    )}
                                {errors.fk_followUpConsultant && ( //FP-14499: Show validation error for consultant field if there is one and any 24-hour follow-up is selected
                                         <div
                                            style={{
                                               color: 'red',
                                               fontSize: '12px',
                                               marginTop: '35px',
                                               position: 'absolute',
                                               width:"155px"
                                             }}
                                           >
                                             {errors.fk_followUpConsultant + ''}
                                          </div>
                                )}
                                </div>
                            </span>
                            <span className="form-group" style={{ margin: '10px' }}>
                                <label style={{ minWidth: '80px' }} htmlFor="ts_FollowUpHandoff">Hand Off*:</label>{/*FP-14499: Add asterisk to indicate required field when any 24-hour follow-up is selected*/}
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '0.5rem'
                                    }}
                                >
                                    <DatePicker
                                        id="ts_FollowUpHandoff"
                                        name="ts_FollowUpHandoff"
                                        dateFormat="MM/dd/yyyy HH:mm"
                                        inputClass="ntsp-fields"
                                        timeformat="HH:mm"
                                        showMasking={true}
                                        showTime={true}
                                        startingDate={form.ts_FollowUpHandoff ? new Date(form.ts_FollowUpHandoff) : null}
                                        setStartingDate={(dateString) => {
                                            handleChange({
                                                target: {
                                                    name: 'ts_FollowUpHandoff',
                                                    value: dateString,
                                                    type: 'text'
                                                }
                                            })
                                            if (handleBlur) {
                                                const event = {
                                                    target: {
                                                        name: 'ts_FollowUpHandoff',
                                                        value: dateString,
                                                        type: 'text',
                                                    },
                                                }
                                                handleBlur(event as any)
                                            }
                                        }}
                                        placeholder="Select Date & Time"
                                        isClearable={true}
                                        calendarClosed={true}
                                        tabIndex={31}
                                        style={{ width: '160px' }}
                                    />
                                    <button
                                        type="button"
                                        className="d-inline-block app-button"
                                        onClick={() => {
                                            const selectedFacility = NTSPElements?.Facilities?.find(
                                                f => f._key === form.fk_Facility
                                            )
                                            const ianaTz = selectedFacility?.TZCode
                                                ? tzCodeToIANA(selectedFacility.TZCode)
                                                : undefined
                                            const now = ianaTz
                                                ? moment.tz(new Date(), ianaTz)
                                                : moment(new Date())
                                            setForm(prev => ({
                                                ...prev,
                                                ts_FollowUpHandoff: now.format('YYYY-MM-DDTHH:mm')
                                            }))
                                            setErrors((prev: FieldErrors) => ({//FP-14499: Clear validation error for handoff field when user clicks "Now" since they are providing a valid value
                                                ...prev,
                                                ts_FollowUpHandoff: '',
                                            }))
                                        }}
                                        tabIndex={32}
                                        style={{ width: '80px' }}
                                    >
                                        Now
                                    </button>
                                {errors.ts_FollowUpHandoff && (//FP-14499: Show validation error for handoff field if there is one and any 24-hour follow-up is selected
                                    <div
                                    style={{
                                        color: 'red',
                                        fontSize: '12px',
                                        marginTop: '35px',
                                        position: 'absolute',
                                        whiteSpace: 'nowrap'
                                    }}
                                    >
                                        {errors.ts_FollowUpHandoff + ''}
                                    </div>
                                )}
                                </div>
                            </span>
                        </div>
                    )}
                </div>
                    {form.fk_ConsultService===teleEEGServiceKey && ( 
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                fontWeight: 500,
                                fontSize: 16,
                                cursor: 'pointer',
                                marginLeft: '-5px',
                                padding: '5px',
                                marginBottom: '1.5rem',
                            }}
                            onClick={() => setOpenSymptoms((v) => !v)}
                        >
                            <span
                                style={{
                                    fontSize: 14,
                                    marginRight: 8,
                                    userSelect: 'none',
                                    transition: 'transform 0.2s',
                                    transform: toggleSymptoms ? 'rotate(90deg)' : 'rotate(0deg)',
                                }}
                            >
                                ▶
                            </span>
                            Relevant History
                        </div>
                        )}
                        {form.fk_ConsultService === teleEEGServiceKey && toggleSymptoms && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '15px 45px',
                                    flexWrap: 'wrap',
                                }}
                            >
                                {/* CategoryGroupedDropdown */}
                                {Object.keys(typeMap)?.map((type) => (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '1rem',
                                            width: '21%',
                                            minWidth: 'fit-content',
                                        }}
                                    >
                                        <div className="form-group cursor-pointer">
                                            <label
                                                className={
                                                    ''
                                                }
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                {type} <br />
                                                Symptoms:
                                            </label>
                                            <span
                                                className={
                                                    'ntsp-fields'
                                                }
                                                style={{ padding: '0px', border: 'none' }}
                                                title={teleEEGServiceKey === form.fk_ConsultService && type !== 'EEG' ? 'Disabled because the consult service is Tele-EEG' : ''}
                                            >
                                                <Select
                                                    isMulti
                                                    options={([
                                                        ...(typeMap[type][''] || []),
                                                        ...Object.keys(typeMap[type])
                                                            .filter((c) => c !== '')
                                                            .map((category) => ({
                                                                label: category,
                                                                options: typeMap[type][category],
                                                            })),
                                                    ]) as any}
                                                    components={{ Group: SymptomGroup as any }}
                                                    value={selectedSymptoms[type] || []}
                                                    onChange={(newValue) =>
                                                        updateSelectedSymptoms(newValue, type)
                                                    }
                                                    styles={customStyles}
                                                    closeMenuOnSelect={false}
                                                    placeholder={'Select Symptoms'}
                                                    // disabled no longer needed due to filtering logic
                                                    isDisabled={false}
                                                    tabIndex={28 + Object.keys(typeMap).indexOf(type)}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                {/* Column 3 */}
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1rem',
                                        width: '30%',
                                        minWidth: 'fit-content',
                                    }}
                                ></div>
                            </div>
                        )}
                        <div
                            style={{
                                position: 'relative',
                                marginTop:
                                    form.fk_ConsultService === SabacuteServiceKey ? '9px' : '5px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label htmlFor="NewNote" className="mb-1" style={{ marginBottom: 0 }}>
                                    New Provider Note:
                                </label>
                            </div>
                            <textarea
                                id="NewNote"
                                className="w-100 mt-2 ntsp-fields"
                                style={{ height: '5rem', paddingBottom: '1.5rem', cursor: isEditingProviderNote ? 'not-allowed' : 'text' }}
                                name="NewNote"
                                maxLength={2048}
                                placeholder="Add Note"
                                value={form.NewNote || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                tabIndex={20}
                                disabled={isEditingProviderNote} // Disable textarea when editing an existing note to prevent confusion between editing and adding new note
                            />
                            {remainigNotes !== 2048 && form?.NewNote?.length > 0 && !isEditingProviderNote && (
                                <div
                                    style={{
                                        fontSize: '10px',
                                        color: '#ff0000',
                                        textAlign: 'right',
                                        marginTop: '4px',
                                        paddingRight: '4px',
                                        position: 'absolute',
                                        right: 0,
                                    }}
                                    id="NotesRemainingLabel"
                                >
                                    {remainigNotes} characters remaining
                                </div>
                            )}
                            {errors.NewNote && (
                                <div
                                    style={{
                                        color: 'red',
                                        fontSize: '12px',
                                        marginTop: '4px',
                                        maxWidth: '350px',
                                        minWidth: '200px',
                                        position: 'absolute'
                                    }}
                                >
                                    {errors.NewNote + ''}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginTop: '1.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label htmlFor="ExamCompleted" style={{ margin: 0 }}>
                                        Exam Completed:
                                    </label>
                                    <input
                                        className="ntsp-fields"
                                        type="checkbox"
                                        name="ExamCompleted"
                                        checked={form.ExamCompleted}
                                        onChange={handleChange}
                                        id="ExamCompleted"
                                        tabIndex={21}
                                        disabled={editingProviderNoteId !== null}
                                    />
                                </div>

                                {/*
                                    Enable and require CPT Codes only if:
                                    - Service line is Acute, Subacute, or Neurohospitalist
                                    - Outcome ETLMatchValue is 'completed'
                                */}
                                {(() => {
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '500px' , maxWidth: '650px'}}>
                                            <label htmlFor="fk_CPTCode" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                                                CPT Codes
                                                {isMandatory && (
                                                    <span>*</span>
                                                )}:
                                            </label>
                                            <div style={{ flex: 1, position: 'relative' }}>
                                                <Select
                                                    options={NTSPElements?.CPTCodes?.map((cptCode: NTSPElement) => ({
                                                        label: cptCode.FriendlyName,
                                                        value: cptCode._key.toString()
                                                    })) || []}
                                                    value={selectedCPTCodes}
                                                    onChange={(newValue) => updateSelectedCPTCodes(newValue)}
                                                    styles={compactCPTStyles}
                                                    components={{ MultiValueRemove: CPTMultiValueRemove }}
                                                    isMulti
                                                    isClearable={selectedCPTCodes.some((option) => !option.isFixed)}
                                                    isSearchable
                                                    isOptionDisabled={(option) =>
                                                        lockedProviderCPTCodeIdSet.has(Number(option.value))
                                                    }
                                                    placeholder="Select CPT Code(s)"
                                                    tabIndex={22}
                                                    id="fk_CPTCode"
                                                    menuPlacement="auto"
                                                    maxMenuHeight={300}
                                                    onBlur={checkCPTcode}
                                                />
                                                {errors.fk_CPTCode && (
                                                    <div
                                                        style={{
                                                            color: 'red',
                                                            fontSize: '12px',
                                                            marginTop: '4px',
                                                            position: 'absolute',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {errors.fk_CPTCode + ''}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        
                        </div>
                <div style={{ marginTop: '8px' }}>
                    <label htmlFor="ClinicalNotes" className="mb-1">
                        Provider Clinical Notes:
                    </label>
                    <div
                        className='provider-clinical-notes mt-2'
                    >
                        <div className="tab_section">
                            <span className="ntsp_tab-buttons w-100">
                            {providerNotes?.map((noteGroup, idx) => {
                                let tabLabel = '';
                                if (NTSPElements?.FollowUp && noteGroup.fk_FollowUp!=-1) {
                                    const followUpObj = NTSPElements.FollowUp.find(f => f._key === noteGroup.fk_FollowUp);
                                    tabLabel = followUpObj ? followUpObj.FriendlyName :"Followup";
                                }
                                else if (NTSPElements?.ConsultServices){
                                     const serviceLineObj = NTSPElements.ConsultServices.find(f => f._key === noteGroup.fk_ConsultService);
                                    tabLabel = serviceLineObj ? serviceLineObj.FriendlyName:getServiceLineName(form.fk_ConsultService);
                                }
                                return (
                                    <button
                                        key={noteGroup.CaseId}
                                        type='button'
                                        // className= ''{ 
                                        //     'pcn-tab' +
                                        //     (noteGroup.CaseId === selectedCaseId ? ' active' : '')
                                        // }
                                        style={{minWidth:"15rem"}}
                                        className={`py-2 ntsp_tab-button notes-tab ${selectedCaseId === noteGroup.CaseId ? 'active' : ''}`}
                                        onClick={() => setSelectedCaseId(noteGroup.CaseId)}
                                    >
                                        {tabLabel}
                                        <br />
                                        {noteGroup.CaseId !== form.CaseId ? (
                                            <>
                                                Case : #{getCaseIdSuffix(noteGroup.CaseId)}
                                            </>
                                        ) : (
                                            <div> (current)</div>
                                        )}
                                    </button>
                                );
                            })}
                            </span>
                        </div>
                        <div className='clinical-Notes pcn-notes-area'>
                            {
                                providerNotes
                                    ?.find(n => n.CaseId === selectedCaseId)
                                    ?.ProviderCaseNotes
                                    .slice()
                                    .sort((a, b) => {
                                        const diagnosisPrefix = 'diagnosis discussed with patient at'
                                        const aIsDiagnosis = (a.Note || '').trim().toLowerCase().startsWith(diagnosisPrefix)
                                        const bIsDiagnosis = (b.Note || '').trim().toLowerCase().startsWith(diagnosisPrefix)

                                        const aTime = parseNoteTimestamp(a.ts_CreationDateTime)
                                        const bTime = parseNoteTimestamp(b.ts_CreationDateTime)

                                        if (aTime !== null && bTime !== null && aTime !== bTime) {
                                            return bTime - aTime
                                        }

                                        if (aIsDiagnosis !== bIsDiagnosis) {
                                            return aIsDiagnosis ? -1 : 1
                                        }

                                        return 0
                                    })
                                    .map((note, idx) => {
                                        const noteKey =
                                            typeof note.Id === 'number' && note.Id > 0
                                                ? `note-${note.Id}`
                                                : `note-unsaved-${note.ts_CreationDateTime || 'na'}-${idx}`
                                        const isPatientEducationNote =
                                            (note.Note || '').trim().toLowerCase().startsWith('(patient education)')
                                        const showCPTPrefix =
                                            !isPatientEducationNote &&
                                            Array.isArray(note?.fk_CPTCodes) &&
                                            note?.fk_CPTCodes?.length > 0
                                        const noteHasCptCodes = Array.isArray(note?.fk_CPTCodes) && note?.fk_CPTCodes?.length > 0
                                        const canEditNote =
                                            providerNoteEditFeatureEnabled &&
                                            noteHasCptCodes &&
                                            isNoteWithinEditWindow(note?.ts_CreationDateTime) &&
                                            isEditableByCurrentUser(note, currentUserId)
                                        const isEditingCurrentNote =
                                            editingProviderNoteCaseId === selectedCaseId &&
                                            editingProviderNoteId === note?.Id
                                        // Get facility time zone
                                        const selectedFacility = NTSPElements?.Facilities?.find(f => f._key === form.fk_Facility);
                                        const ianaTz = selectedFacility?.TZCode ? tzCodeToIANA(selectedFacility.TZCode) : undefined;
                                        const noteTime =moment(convertEasternToLocal(note.ts_CreationDateTime, ianaTz))?.format('MM/DD/YYYY HH:mm') ;
                                        return (
                                            <div
                                                className='pcn-note'
                                                key={noteKey}
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(180px, 180px) 1fr',
                                                    alignItems: 'start',
                                                    marginBottom: '0.35rem',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                <span style={{ color: '#888', fontSize: '12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                    {note.PostedByUserFullName}
                                                    {noteTime && (
                                                        <span style={{ color: '#888', fontSize: '12px', marginLeft: 6 }}>
                                                            ({noteTime})
                                                        </span>
                                                    )}
                                                </span>
                                                <span style={{ marginLeft: 6}}>
                                                    <span style={{ whiteSpace: 'pre-wrap' }}>
                                                        {note.ExamCompleted ?  '(Exam Completed) ' : ''}
                                                        {showCPTPrefix ? `(Selected CPT Codes: ${note.fk_CPTCodes
                                                            .map(code => NTSPElements?.CPTCodes?.find(c => c._key === code)?.FriendlyName || code)
                                                            .join(', ')}) ` : ''}
                                                        {note.Note}
                                                    </span>
                                                    {canEditNote && (
                                                        <button
                                                            type='button'
                                                            onClick={() =>
                                                                isEditingCurrentNote
                                                                    ? cancelProviderNoteEdit()
                                                                    : startProviderNoteEdit(selectedCaseId, note)
                                                            }
                                                            title={
                                                                isEditingCurrentNote
                                                                    ? 'Cancel editing this note'
                                                                    : 'Edit note (available for 8 hours from creation)'
                                                            }
                                                            style={{
                                                                border: 'none',
                                                                background: 'transparent',
                                                                color: isEditingCurrentNote
                                                                    ? (isNightMode ? '#f8f9fa' : '#212529')
                                                                    : (isNightMode ? '#adb5bd' : '#495057'),
                                                                cursor: 'pointer',
                                                                padding: 0,
                                                                marginLeft: 8,
                                                                lineHeight: 1,
                                                                display: 'inline-flex',
                                                                verticalAlign: 'baseline',
                                                            }}
                                                        >
                                                            {isEditingCurrentNote ? '✕' : '✎'}
                                                        </button>
                                                    )}
                                                </span>
                                            </div>
                                        );
                                    })
                            }
                        </div>
                    </div>
                </div>
                <div style={{ marginTop: '8px' }}>
                            <label htmlFor="ClinicalNotes" className="mb-1">
                            Nurse Clinical Notes:
                            </label>
                            <div
                                id="NurseClinicalNotes"
                                className="w-100 mt-2 clinical-Notes"
                                style={{
                                    height: '8rem',
                                    cursor: 'not-allowed',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                <span>
                            {nurseNotes?.map((note, idx) => {
                                const nurseNoteKey =
                                    typeof note.Id === 'number' && note.Id > 0
                                        ? `nurse-note-${note.Id}`
                                        : `nurse-note-unsaved-${note.ts_CreationDateTime || 'na'}-${idx}`
                                // Get facility time zone
                                const selectedFacility = NTSPElements?.Facilities?.find(f => f._key === form.fk_Facility);
                                const ianaTz = selectedFacility?.TZCode ? tzCodeToIANA(selectedFacility.TZCode) : undefined;
                                const noteTime = note.ts_CreationDateTime
                                    ? (ianaTz
                                        ? moment.tz(note.ts_CreationDateTime, ianaTz).format('MM/DD/YYYY HH:mm')
                                        : moment(note.ts_CreationDateTime).format('MM/DD/YYYY HH:mm'))
                                    : '';
                                return (
                                    <div
                                        className='pcn-note'
                                        key={nurseNoteKey}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(180px, 180px) 1fr',
                                            alignItems: 'center',
                                            //gap: '1rem',
                                            marginBottom: '0.35rem',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        <span style={{ color: '#888', fontSize: '12px', fontWeight: 500, display: 'inline-block' }}>
                                            {note.PostedByUserFullName}
                                            {noteTime && (
                                                <span style={{ color: '#888', fontSize: '12px', marginLeft: 6 }}>
                                                    ({noteTime}):
                                                </span>
                                            )}
                                        </span>
                                        <span style={{ whiteSpace: 'pre-wrap', marginLeft: 6 }}>
                                            {note.Note}
                                        </span>
                                    </div>
                                );
                            })}
                        </span>
                            </div>
                        </div>
                {/* Buttons */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '1.2rem',
                        marginTop: '2rem',
                    }}
                >
                    <FoundationsButton
                        type="submit"
                        buttonClasses="app-button"
                        tabIndex={30}
                        disabled={saving}
                        id={''}
                    >
                        Save
                    </FoundationsButton>
                    <FoundationsButton
                        type="button"
                        buttonClasses="app-button"
                        onClick={wrapHandleClear}
                        buttonRef={lastInputRef}
                        id={''}
                        toolTipText="Currently opened case entry form will be saved and closed."
                        tooltipClasses="info-icon"
                        tabIndex={31}
                    >
                        Close
                    </FoundationsButton>
                </div>
                {submitted && formError !== '' && (
                    <div style={{ color: 'red', textAlign: 'right', marginTop: '1rem' }}>
                        {formError}
                    </div>
                )}
            </form>
        </>
    )
}

export default CaseDetailsForm
